import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  RotateCcw,
  Check,
  Search,
  FileText,
  Mic,
  Square,
  Wand2,
  X,
  Eraser,
  Copy,
  Moon,
  Sun,
  SunMoon,
  ShieldCheck,
  Star,
  Heart,
  Pencil,
  Plus,
  Activity,
  GraduationCap,
  Zap,
  Sparkles,
  Timer,
  RotateCw,
  AlignVerticalJustifyCenter,
  HelpCircle,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sliders,
  Circle,
  CheckCircle2,
  Anchor,
  Clock,
  TrendingUp,
} from "lucide-react";
import { collapseGenerationEventsBySession } from "../utils/sessionStats";
import { ReportTemplate, LANGUAGES, ROIEventType, Phrase } from "../types";
import {
  generateStructuredSections,
  transcribeAudio,
  reconcileMedicalTranscription,
  postProcessReport,
} from "../services/geminiService";
import { AudioRecorder, blobToBase64 } from "../services/audioUtils";
import {
  copyHtmlToClipboard,
  copyToClipboard,
} from "../services/clipboardUtils";
import {
  incrementReportUsage,
  recordROIEvent,
  streamTextToCloud,
  isMobileDevice,
  toggleFavoriteReportCloud,
  saveModuleSectionHistory,
  clearModuleSessions,
  subscribeToModuleSession,
  saveModular1Favorites,
  subscribeToModular1Favorites,
  subscribeToUserProfile,
  subscribeToRegionalRestrictions,
  recordDraftingUsage,
  subscribeToPhrases,
  saveModular1GenerationSnapshot,
  subscribeToModular1GenerationLog,
  fetchUserRealStats,
} from "../services/db";
import { auth } from "../firebaseConfig";
import { sanitizeHtml } from "../utils/sanitize";
import { saveTranscriptionBackup, deleteTranscriptionBackup, FailedTranscription } from "../services/transcriptionBackupService";
import RetryTranscriptionButton from "./RetryTranscriptionButton";
import HelpTooltip from "./HelpTooltip";
import { getRegionalAccess } from "../utils/regionalAccess";

import { runOnce, TAB_ID } from "../services/syncService";

const mdToHtml = (text: string) => {
  if (!text) return "";
  
  // 1. Normalizamos los saltos de línea y limpiamos escapes literales de la IA
  let processed = text
    .replace(/\\n/g, "\n")
    .replace(/\\\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/&nbsp;/g, " ");
  
  // 2. Limpieza de indentaciones y caracteres prohibidos al inicio de cada línea
  // Preservamos líneas que parecen HTML (tablas)
  processed = processed.split("\n").map(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith('<') || trimmed.endsWith('>')) {
      return line;
    }
    return line
      .trimStart() // Elimina sangrías/espacios al inicio
      .replace(/^[\s*•\-\d.]+\s*/, "") // Elimina viñetas, guiones, números de lista al inicio
      .replace(/^["'](.*)["']$/, "$1"); // Elimina comillas que envuelven la línea
  }).join("\n");

  // 3. Limpiar asteriscos y comillas residuales (solo fuera de etiquetas HTML)
  let cleaned = "";
  let inTag = false;
  for (let i = 0; i < processed.length; i++) {
    const char = processed[i];
    if (char === '<') inTag = true;
    if (!inTag) {
      if (char !== '*' && char !== '"') {
        cleaned += char;
      }
    } else {
      cleaned += char;
    }
    if (char === '>') inTag = false;
  }

  // 4. Convertir Markdown Bold a HTML
  let html = cleaned.replace(/(\*\*|__)(.*?)\1/g, "<b>$2</b>");
  
  // 5. Dividir por párrafos (doble salto de línea)
  const paragraphs = html.split(/\n\n+/);
  
  return paragraphs.map(p => {
    const trimmed = p.trim();
    // Si el párrafo es una tabla, lo devolvemos tal cual
    if (trimmed.startsWith('<table') || trimmed.startsWith('<thead') || trimmed.startsWith('<tbody') || trimmed.startsWith('<tr')) {
      return p;
    }
    
    // Para cada párrafo normal, manejar los saltos de línea simples
    const lines = p.split('\n');
    return `<p style="margin-bottom: 1em;">${lines.join('<br>')}</p>`;
  }).join('');
};

import AIAssistantMenu from "./AIAssistantMenu";

const ResultBox = React.memo(({
  title,
  helpText,
  content,
  icon,
  color,
  onAudit,
  onPostProcess,
  onRestoreOriginal,
  onNavigateHistory,
  isDarkMode,
  isProcessing,
  canRestore,
  versionIndex = 0,
  historyCount = 1
}: any) => {
  const [copied, setCopied] = useState(false);

  // MEMOIZACIÓN DEL HTML (Evita re-procesamiento regex innecesario)
  const htmlContent = useMemo(() => mdToHtml(content), [content]);

  const handleCopyClick = async () => {
    if (!content) return;
    const savedVal = localStorage.getItem("modular_copy_rich_text");
    const useRichText = savedVal === null ? true : savedVal === "true";
    let success = false;
    if (useRichText) {
      success = await copyHtmlToClipboard(htmlContent);
    } else {
      const plainText = content
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<[^>]*>?/gm, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\*\*/g, "")
        .trim();
      success = await copyToClipboard(plainText);
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const colorClasses: any = {
    blue: isDarkMode
      ? "border-l-slate-600 text-slate-400 bg-slate-900/10"
      : "border-l-slate-400 text-slate-700 bg-slate-50",
    green: isDarkMode
      ? "border-l-green-600 text-green-400 bg-blue-900/10"
      : "border-l-green-500 text-green-700 bg-green-50",
    purple: isDarkMode
      ? "border-l-purple-600 text-purple-400 bg-blue-900/10"
      : "border-l-purple-500 text-purple-700 bg-purple-50",
    red: isDarkMode
      ? "border-l-red-600 text-red-400 bg-blue-900/10"
      : "border-l-red-500 text-red-700 bg-red-50",
  };

  if (!content) return null;

  return (
    <div
      className={`rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md group relative ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-gray-200"}`}
    >
      <div
        className={`px-4 py-3 border-l-4 ${colorClasses[color]} flex flex-wrap justify-between items-center gap-y-3 bg-opacity-30`}
      >
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
          <div className="flex items-center space-x-2 font-bold text-sm shrink-0">
            {icon}
            <span>{title}</span>
            {helpText && (
              <HelpTooltip text={helpText} isDarkMode={isDarkMode} iconSize={14} />
            )}
          </div>

          {onPostProcess && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className={`hidden sm:block w-px h-4 ${isDarkMode ? 'bg-gray-600/50' : 'bg-gray-300/50'}`} />
              <AIAssistantMenu 
                isDark={isDarkMode}
                versionIndex={versionIndex}
                historyCount={historyCount}
                onNavigateHistory={onNavigateHistory}
                onPostProcess={onPostProcess}
                isProcessing={isProcessing}
                onRestoreOriginal={onRestoreOriginal}
                onCopy={handleCopyClick}
                showCopy={true}
              />
            </div>
          )}
        </div>


        <div className="flex items-center space-x-2">
          {isProcessing && (
            <div className="mr-2">
              <RotateCw size={12} className="animate-spin text-indigo-500" />
            </div>
          )}
        </div>
      </div>
      <div
        onClick={handleCopyClick}
        className={`w-full p-4 text-sm leading-relaxed relative cursor-pointer transition-colors rad-html-content font-medium ${isDarkMode ? "text-gray-300 bg-transparent hover:bg-gray-800" : "text-slate-900 bg-white hover:bg-gray-50"}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
      />
      {copied && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
          <div className="bg-green-600 text-white text-[10px] font-black px-4 py-2 rounded-full shadow-xl flex items-center space-x-2 animate-in zoom-in fade-in duration-200">
            <Check size={14} strokeWidth={3} />
            <span>¡COPIADO!</span>
          </div>
        </div>
      )}
      {title === "HALLAZGOS" && onAudit && (
        <div
          className={`px-4 py-2 border-t flex justify-end ${isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-50 border-gray-100"}`}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAudit();
            }}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all shadow-sm active:scale-95 ${isDarkMode ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
          >
            <ShieldCheck size={14} />
            <span>Auditar PhD</span>
          </button>
        </div>
      )}
    </div>
  );
});

const buildAnchorInsertion = (before: string, rawText: string, after: string): string => {
  let text = rawText.trim();
  if (!text) return '';

  const beforeTrimmed = before.replace(/\s+$/, '');
  const isNewSentence = beforeTrimmed.length === 0 || /[.?!]$/.test(beforeTrimmed) || /\n\s*$/.test(before);

  // Mayúscula si es inicio de oración/párrafo, minúscula si continúa una oración a medias
  text = isNewSentence
    ? text.charAt(0).toUpperCase() + text.slice(1)
    : text.charAt(0).toLowerCase() + text.slice(1);

  // Separador antes: solo si before no termina ya en espacio/salto de línea
  const prefix = (before.length > 0 && !/\s$/.test(before)) ? ' ' : '';

  // Separador después: solo si after no empieza ya en espacio/salto de línea
  const suffix = (after.length > 0 && !/^\s/.test(after)) ? ' ' : '';

  return prefix + text + suffix;
};

interface Modular1ScreenProps {
  reports: ReportTemplate[];
  onSavePhrase?: (text: string) => void;
  initialReportId: string | null;
  initialText: string;
  onEditReport?: (reportId: string) => void;
}

const Modular1Screen: React.FC<Modular1ScreenProps> = ({
  reports,
  initialReportId,
  initialText,
  onEditReport,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handleGenerateReportRef = useRef<() => void>(() => {});
  const topAnchorRef = useRef<HTMLDivElement>(null);
  const reportSearchContainerRef = useRef<HTMLDivElement>(null);
  const phraseSearchContainerRef = useRef<HTMLDivElement>(null);

  const [userProfile, setUserProfile] = useState<any>(null);
  const [regionalRestrictions, setRegionalRestrictions] = useState<any[]>([]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsubProfile = subscribeToUserProfile(uid, setUserProfile);
    const unsubRestrictions = subscribeToRegionalRestrictions(setRegionalRestrictions);
    return () => {
      unsubProfile();
      unsubRestrictions();
    };
  }, []);

  const activeRegionalSettings = useMemo(() => {
    if (!userProfile) return { renameConclusionToSummary: false, disableDiferenciales: false };
    const userEmail = (auth.currentUser?.email || '').toLowerCase();
    const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || auth.currentUser?.uid === 'guest_dev_offline';
    const isCurrentlyAdmin = isMaster || userProfile?.role === 'ADMIN';
    return getRegionalAccess(userProfile, regionalRestrictions, (isCurrentlyAdmin || !!userProfile?.internalTestingBypass));
  }, [userProfile, regionalRestrictions]);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("module_theme_modular1");
    if (saved) return saved === "dark";
    return localStorage.getItem("app_theme") === "dark";
  });

  const [globalThemeState, setGlobalThemeState] = useState<'day' | 'mixed' | 'dark'>(() => {
    return (localStorage.getItem("app_theme") as 'day' | 'mixed' | 'dark') || 'day';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const currentGlobal = (localStorage.getItem("app_theme") as 'day' | 'mixed' | 'dark') || 'day';
      setGlobalThemeState(currentGlobal);
      const saved = localStorage.getItem("module_theme_modular1");
      if (!saved) {
        setIsDarkMode(localStorage.getItem("app_theme") === "dark");
      }
    };
    window.addEventListener("app_theme_changed", handleThemeChange);
    return () =>
      window.removeEventListener("app_theme_changed", handleThemeChange);
  }, []);

  const [generatedResult, setGeneratedResult] = useState<{
    findings: string;
    conclusion: string;
    differential: string;
  } | null>(() => {
    const saved = localStorage.getItem("modular1_last_result");
    return saved ? JSON.parse(saved) : null;
  });

  const [sectionHistories, setSectionHistories] = useState<Record<string, string[]>>(() => {
    const saved = localStorage.getItem("modular1_section_histories");
    return saved ? JSON.parse(saved) : { findings: [], conclusion: [], differential: [] };
  });

  const [sectionIndices, setSectionIndices] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem("modular1_section_indices");
    return saved ? JSON.parse(saved) : { findings: -1, conclusion: -1, differential: -1 };
  });

  const [generationHistory, setGenerationHistory] = useState<Array<{
    id: string;
    timestamp: number;
    selectedReportId: string | null;
    fullText: string;
    generatedResult: any;
    includeTitle: boolean;
    fusionMode: 'fast' | 'normal';
    flexibleMode: boolean;
  }>>(() => {
    const saved = localStorage.getItem("modular1_generation_history");
    return saved ? JSON.parse(saved) : [];
  });
  const [generationIndex, setGenerationIndex] = useState<number>(() => {
    const saved = localStorage.getItem("modular1_generation_history");
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        return Array.isArray(arr) && arr.length > 0 ? arr.length - 1 : -1;
      } catch {
        return -1;
      }
    }
    return -1;
  });

  useEffect(() => {
    localStorage.setItem("modular1_generation_history", JSON.stringify(generationHistory));
  }, [generationHistory]);

  // NUEVOS ESTADOS PARA FUNCIONES PhD E INLINE AUDIT (Ajuste Senior Architecture)
  const [inlineAuditResult, setInlineAuditResult] = useState<{ rating: number; recommendations: string } | null>(null);
  const [isInlineAuditing, setIsInlineAuditing] = useState(false);
  const [isIntegratingAudit, setIsIntegratingAudit] = useState(false);

  const [rawAuditResult, setRawAuditResult] = useState<{ rating: number; recommendations: string } | null>(null);
  const [isRawAuditing, setIsRawAuditing] = useState(false);
  const [isApplyingAudit, setIsApplyingAudit] = useState(false);
  const [isGeneratingConclusion, setIsGeneratingConclusion] = useState(false);

  const [mod1Favorites, setMod1Favorites] = useState<string[]>(() => {
    const saved = localStorage.getItem("modular1_exclusive_favorites");
    return saved ? JSON.parse(saved) : [];
  });

  const [hasLoadedFromCloud, setHasLoadedFromCloud] = useState(false);

  const [selectedReportId, setSelectedReportId] = useState<string | null>(
    initialReportId || null,
  );
  
  // GUARD PARA CONSUMO DE PROPS EXTERNAS (Evita el bug de "Sticky Report")
  const lastConsumedPropRef = useRef({ id: initialReportId, text: initialText });

  useEffect(() => {
    if (initialReportId !== lastConsumedPropRef.current.id) {
      setSelectedReportId(initialReportId);
      lastConsumedPropRef.current.id = initialReportId;
    }
  }, [initialReportId]);

  useEffect(() => {
    // Solo actualizar si el texto cambio y no es un eco de lo que ya tenemos
    if (initialText !== lastConsumedPropRef.current.text) {
      setFullText(initialText);
      lastConsumedPropRef.current.text = initialText;
    }
  }, [initialText]);

  const [reportSearch, setReportSearch] = useState("");
  const [fullText, setFullText] = useState(initialText || "");
  const [localFullText, setLocalFullText] = useState(initialText || "");
  const isTypingRef = useRef(false);

  // Sincronizar estado local con externo de forma controlada
  useEffect(() => {
    if (!isTypingRef.current && fullText !== localFullText) {
      setLocalFullText(fullText);
    }
  }, [fullText]);

  // Propagar cambios locales al estado global con un ligero debounce para evitar saturación de red/broadcast
  useEffect(() => {
    if (localFullText === fullText) return;
    const timer = setTimeout(() => {
      setFullText(localFullText);
    }, 100); // Latencia mínima imperceptible
    return () => clearTimeout(timer);
  }, [localFullText]);
  const [outputLang] = useState("Español");
  const [fusionMode, setFusionMode] = useState<'fast' | 'normal'>(() => {
    const saved = localStorage.getItem("modular1_fusion_mode");
    return (saved === 'fast' || saved === 'normal') ? (saved as 'fast' | 'normal') : 'fast';
  });
  const [flexibleMode, setFlexibleMode] = useState<boolean>(() => {
    return localStorage.getItem("modular1_flexible_mode") === "true";
  });
  const [includeTitle, setIncludeTitle] = useState<boolean>(() => {
    return localStorage.getItem("modular1_include_title") === "true";
  });
  const [showFusionHelp, setShowFusionHelp] = useState(false);

  const [anchorMode, setAnchorMode] = useState(false);
  const anchorPosRef = useRef<number>(0);
  const lastCursorPosRef = useRef<number>(0);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [isRemoteLocked, setIsRemoteLocked] = useState(false);
  const [liveStreamText, setLiveStreamText] = useState("");

  const [lastGenerationTiming, setLastGenerationTiming] = useState<{ dictationSeconds: number; fusionSeconds: number } | null>(null);
  const [monthlyAvgTiming, setMonthlyAvgTiming] = useState<{ avgDictation: number; avgFusion: number; count: number } | null>(null);

  // Sincronización entre instancias para evitar doble envío (Arquitectura Senior)
  useEffect(() => {
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.onmessage = (event) => {
      if (event.data.type === 'RECORDING_START' && event.data.senderId !== TAB_ID) {
        setIsRemoteLocked(true);
      } else if (event.data.type === 'RECORDING_STOP' && event.data.senderId !== TAB_ID) {
        setIsRemoteLocked(false);
      }

      if (
        event.data.type === "RAD1_AGENT_AUTO" &&
        event.data.text !== undefined
      ) {
        setFullText(event.data.text);
        setGeneratedResult(null);
        setProcessSeconds(0);
        setIsProcessTimerRunning(false);
        setSelectedReportId(event.data.reportId || null);
        setFusionMode(event.data.fusionMode === 'normal' ? 'normal' : 'fast');
        setFlexibleMode(!!event.data.flexibleMode);
        setTimeout(() => {
          handleGenerateReportRef.current();
        }, 700);
      }
    };
    return () => bc.close();
  }, []);
  const [engineMode, setEngineMode] = useState<"phd" | "gm1">(
    () => {
      const saved = localStorage.getItem("modular1_engine_mode");
      return saved === "phd" ||
        saved === "gm1"
        ? (saved as any)
        : "gm1";
    },
  );

  const [processSeconds, setProcessSeconds] = useState(0);
  const [isProcessTimerRunning, setIsProcessTimerRunning] = useState(false);
  const [isPostProcessing, setIsPostProcessing] = useState<string | null>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);

  useEffect(() => {
    if (errorStatus) {
      const timer = setTimeout(() => setErrorStatus(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [errorStatus]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!textareaRef.current) return;
    const savedHeight = localStorage.getItem("modular1_textarea_height");
    if (savedHeight) textareaRef.current.style.height = savedHeight;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = (entry.target as HTMLElement).style.height;
        if (height) localStorage.setItem("modular1_textarea_height", height);
      }
    });
    resizeObserver.observe(textareaRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const isWide = containerWidth > 850;

  // Favoritos exclusivos de MODULAR 1 (Independientes de Biblioteca)
  useEffect(() => {
    localStorage.setItem("modular1_exclusive_favorites", JSON.stringify(mod1Favorites));
    
    // Sincronización silenciosa con Firebase
    const uid = auth.currentUser?.uid;
    // CRITICAL FIX: Solo guardar en la nube si ya hemos sincronizado al menos una vez con ella
    // Esto evita que un estado inicial vacío (de un nuevo dispositivo) sobrescriba los favoritos existentes en la nube
    if (uid && uid !== 'guest_dev_offline' && hasLoadedFromCloud) {
      saveModular1Favorites(uid, mod1Favorites);
    }
  }, [mod1Favorites, hasLoadedFromCloud]);

  // Categorías propias de Modular-1 para organizar informes base (independientes de Biblioteca, solo en este equipo, sin nube)
  const [mod1ReportCategories, setMod1ReportCategories] = useState<{ id: string; name: string; reportIds: string[] }[]>(() => {
    try {
      const saved = localStorage.getItem("modular1_report_categories");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [activeCategoryFilterId, setActiveCategoryFilterId] = useState<string | null>(null);
  const [categoryEditModeId, setCategoryEditModeId] = useState<string | null>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryEditSearch, setCategoryEditSearch] = useState("");

  useEffect(() => {
    setCategoryEditSearch("");
  }, [categoryEditModeId]);

  useEffect(() => {
    localStorage.setItem("modular1_report_categories", JSON.stringify(mod1ReportCategories));
  }, [mod1ReportCategories]);

  const handleCreateCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const newCat = { id: `mod1cat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, name, reportIds: [] as string[] };
    setMod1ReportCategories((prev) => [...prev, newCat]);
    setNewCategoryName("");
    setIsAddingCategory(false);
    setActiveCategoryFilterId(null);
    setCategoryEditModeId(newCat.id);
  };

  const handleDeleteCategory = (id: string) => {
    setMod1ReportCategories((prev) => prev.filter((c) => c.id !== id));
    if (activeCategoryFilterId === id) setActiveCategoryFilterId(null);
    if (categoryEditModeId === id) setCategoryEditModeId(null);
  };

  const handleToggleReportInCategory = (reportId: string) => {
    if (!categoryEditModeId) return;
    setMod1ReportCategories((prev) =>
      prev.map((c) => {
        if (c.id !== categoryEditModeId) return c;
        const has = c.reportIds.includes(reportId);
        return { ...c, reportIds: has ? c.reportIds.filter((id) => id !== reportId) : [...c.reportIds, reportId] };
      })
    );
  };

  // Suscripción a favoritos en la nube
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || uid === 'guest_dev_offline') {
      setHasLoadedFromCloud(true);
      return;
    }

    const unsubscribe = subscribeToModular1Favorites(uid, (cloudFavs) => {
      // Sincronizar con el estado local de forma inteligente
      setMod1Favorites(prev => {
        // Si son idénticos, no actualizar para evitar bucles de renderizado
        if (JSON.stringify(prev) === JSON.stringify(cloudFavs)) return prev;
        
        // Si el estado local está vacío y el de la nube tiene datos, el de la nube manda (Nuevo dispositivo)
        // Si el estado local tiene datos y la nube está vacía, pero aún no habíamos cargado, la nube manda (Usuario sin favoritos antiguos)
        return cloudFavs || [];
      });
      
      // Marcar como cargado para habilitar las escrituras en la nube
      setHasLoadedFromCloud(true);
    });

    return () => unsubscribe();
  }, [auth.currentUser?.uid]);

  const toggleMod1Favorite = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setMod1Favorites(prev => 
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const handleThemeChange = () => {
      const saved = localStorage.getItem("module_theme_modular1");
      if (!saved) {
        setIsDarkMode(localStorage.getItem("app_theme") === "dark");
      }
    };
    window.addEventListener("app_theme_changed", handleThemeChange);
    return () =>
      window.removeEventListener("app_theme_changed", handleThemeChange);
  }, []);

  useEffect(() => {
    localStorage.setItem("modular1_fusion_mode", fusionMode);
  }, [fusionMode]);

  // Ajuste de engineMode si hubiera valores residuales
  useEffect(() => {
    localStorage.setItem('modular1_engine_mode', engineMode);
  }, [engineMode]);

  useEffect(() => {
    localStorage.setItem("modular1_flexible_mode", String(flexibleMode));
  }, [flexibleMode]);

  useEffect(() => {
    localStorage.setItem("modular1_include_title", String(includeTitle));
  }, [includeTitle]);

  const recorderRef = useRef<AudioRecorder | null>(null);
  const liveManagerRef = useRef<any>(null);
  const lastLiveTextRef = useRef("");
  const recordStartTimeRef = useRef<number>(0);
  const continuousRecordingSecondsRef = useRef(0);
  const totalDictationSecondsRef = useRef(0);
  const studySessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const timerRef = useRef<any>(null);
  const processTimerRef = useRef<any>(null);

  // Suscripción a historial de versiones en Firebase
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || uid === 'guest_dev_offline') return;

    const unsubscribe = subscribeToModuleSession(uid, 'modular1', (sessionData) => {
      if (sessionData && sessionData.histories) {
        setSectionHistories(sessionData.histories);
        
        // Sincronizar resultados actuales
        setGeneratedResult(prev => {
          if (!prev) return null;
          const newRes = { ...prev };
          Object.keys(sessionData.histories).forEach(sec => {
            const hist = sessionData.histories[sec];
            if (hist && hist.length > 0) {
              const idx = sectionIndices[sec as keyof typeof sectionIndices] === -1 
                ? hist.length - 1 
                : sectionIndices[sec as keyof typeof sectionIndices];
              if (idx < hist.length) {
                newRes[sec as keyof typeof newRes] = hist[idx];
              }
            }
          });
          return newRes;
        });

        setSectionIndices(prev => {
          const newIndices = { ...prev };
          Object.keys(sessionData.histories).forEach(sec => {
            const s = sec as keyof typeof newIndices;
            if (newIndices[s] === -1) {
              newIndices[s] = sessionData.histories[sec].length - 1;
            }
          });
          return newIndices;
        });
      }
    });

    return () => unsubscribe();
  }, [auth.currentUser?.uid]);

  // Suscripción a historial navegable de informes en Firebase
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || uid === 'guest_dev_offline') return;

    const unsubscribe = subscribeToModular1GenerationLog(uid, (entries) => {
      if (entries && entries.length > 0) {
        setGenerationHistory(entries);
        setGenerationIndex(prev => prev === -1 ? entries.length - 1 : prev);
      }
    });

    return () => unsubscribe();
  }, []);

  // El originalResult ya no es necesario como estado único, se maneja vía resultHistory[0]

  // Sincronizar selección remota o via props de forma controlada
  useEffect(() => {
    const hasIdChanged = initialReportId !== lastConsumedPropRef.current.id;
    const hasTextChanged = initialText !== lastConsumedPropRef.current.text;

    if (hasIdChanged && initialReportId !== selectedReportId) {
      setSelectedReportId(initialReportId);
      setReportSearch("");
      lastConsumedPropRef.current.id = initialReportId;
    }

    if (hasTextChanged && initialText !== fullTextRef.current && document.activeElement !== textareaRef.current) {
      setFullText(initialText);
      lastConsumedPropRef.current.text = initialText;
    }
  }, [initialReportId, initialText, selectedReportId]);

  const fullTextRef = useRef(fullText);
  useEffect(() => {
    fullTextRef.current = fullText;
  }, [fullText]);

  useEffect(() => {
    if (generatedResult) {
      localStorage.setItem(
        "modular1_last_result",
        JSON.stringify(generatedResult),
      );
    } else {
      localStorage.removeItem("modular1_last_result");
    }
  }, [generatedResult]);

  useEffect(() => {
    localStorage.setItem("modular1_section_histories", JSON.stringify(sectionHistories));
  }, [sectionHistories]);

  useEffect(() => {
    localStorage.setItem("modular1_section_indices", JSON.stringify(sectionIndices));
  }, [sectionIndices]);

  // Manejo del cronómetro de proceso
  useEffect(() => {
    if (isProcessTimerRunning) {
      processTimerRef.current = setInterval(() => {
        if (isRecording) {
          continuousRecordingSecondsRef.current += 1;
          totalDictationSecondsRef.current += 1;
          // LÍMITE TÉCNICO: 5 MINUTOS CONTINUOS (300 SEGUNDOS) — se reinicia en cada nueva grabación, no se acumula entre pausas
          if (continuousRecordingSecondsRef.current >= 300) {
            console.warn("Límite de tiempo alcanzado en Modular-1 (5 min continuos).");
            stopRecording();
            return;
          }
        }
        setProcessSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (processTimerRef.current) clearInterval(processTimerRef.current);
    }
    return () => {
      if (processTimerRef.current) clearInterval(processTimerRef.current);
    };
  }, [isProcessTimerRunning, isRecording]);

  const handleTextChange = useCallback((val: string) => {
    setLocalFullText(val);
  }, []);

  const [copiedHallazgos, setCopiedHallazgos] = useState(false);

  const handleCopyHallazgos = useCallback(async () => {
    const textToCopy = localFullText || fullText || "";
    if (!textToCopy.trim()) return;
    const success = await copyToClipboard(textToCopy);
    if (success) {
      setCopiedHallazgos(true);
      setTimeout(() => setCopiedHallazgos(false), 2000);
    }
  }, [localFullText, fullText]);

  const [copiedReportId, setCopiedReportId] = useState<string | null>(null);

  const handleReportSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setReportSearch("");
      setSelectedReportId(null);
    }
  }, []);

  const handleCopyReportContent = useCallback(async (e: React.MouseEvent, report: ReportTemplate) => {
    e.stopPropagation();
    e.preventDefault();
    if (!report?.content) return;
    
    const savedVal = localStorage.getItem("modular_copy_rich_text");
    const useRichText = savedVal === null ? true : savedVal === "true";
    let success = false;
    if (useRichText) {
      const htmlContent = mdToHtml(report.content);
      success = await copyHtmlToClipboard(htmlContent);
    } else {
      const plainText = report.content
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<[^>]*>?/gm, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\*\*/g, "")
        .trim();
      success = await copyToClipboard(plainText);
    }
    if (success) {
      setCopiedReportId(report.id);
      setTimeout(() => {
        setCopiedReportId((prev) => (prev === report.id ? null : prev));
      }, 2000);
    }
  }, []);

  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [phraseSearch, setPhraseSearch] = useState("");
  const [isPhraseSearchOpen, setIsPhraseSearchOpen] = useState(false);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsubPhrases = subscribeToPhrases(uid, setPhrases);
    return () => {
      unsubPhrases();
    };
  }, []);

  const normalizePhraseText = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filteredPhrases = useMemo(() => {
    const q = normalizePhraseText(phraseSearch.trim());
    if (q.length < 3) return [];
    const matches = phrases
      .map((p) => {
        const normalizedText = normalizePhraseText(p.text);
        const idx = normalizedText.indexOf(q);
        if (idx === -1) return null;
        let score = 2;
        if (idx === 0) score = 0;
        else if (normalizedText[idx - 1] === ' ') score = 1;
        return { phrase: p, score, idx };
      })
      .filter((m) => m !== null)
      .sort((a: any, b: any) => a.score - b.score || a.idx - b.idx);
    return matches.map((m: any) => m.phrase);
  }, [phrases, phraseSearch]);

  const handleCopyPhrase = useCallback(async (phraseText: string) => {
    if (!phraseText) return;
    await copyToClipboard(phraseText);
    setIsPhraseSearchOpen(false);
    setPhraseSearch("");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (reportSearchContainerRef.current && reportSearch && !reportSearchContainerRef.current.contains(target)) {
        setReportSearch("");
        setSelectedReportId(null);
      }
      if (phraseSearchContainerRef.current && isPhraseSearchOpen && !phraseSearchContainerRef.current.contains(target)) {
        setIsPhraseSearchOpen(false);
        setPhraseSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [reportSearch, isPhraseSearchOpen]);

  const lastMirroredTimestampRef = useRef<number>(0);

  useEffect(() => {
    const handleMirroredText = (e: any) => {
      const { text, status, timestamp } = e.detail;
      // Solo actuar si es una transcripción final y somos Master (Desktop)
      if (status === 'final' && !isMobileDevice()) {
        // Evitar procesar el mismo mensaje o mensajes antiguos
        if (timestamp && timestamp <= lastMirroredTimestampRef.current) return;
        if (timestamp) lastMirroredTimestampRef.current = timestamp;

        setLocalFullText(prev => {
          // Evitar eco si el texto ya está contenido al final o es idéntico
          const trimmedText = text.trim();
          const trimmedPrev = prev.trim();
          if (trimmedPrev.endsWith(trimmedText) || trimmedPrev === trimmedText) return prev;
          
          const newVal = !prev || trimmedPrev === "" ? text : prev + "\n" + text;
          return newVal;
        });
      }
    };
    window.addEventListener('mirrored_text_update', handleMirroredText);
    return () => window.removeEventListener('mirrored_text_update', handleMirroredText);
  }, [handleTextChange]);

  // SINCRONIZACIÓN DE TEXTO DEBOUNCED (Mejora de latencia)
  useEffect(() => {
    if (!fullText) return;
    const timeout = setTimeout(() => {
      const bc = new BroadcastChannel("modular_sync_channel");
      bc.postMessage({ type: "RAD1_MIRROR_TEXT", text: fullText, senderId: TAB_ID });
      bc.close();
    }, 500);
    return () => clearTimeout(timeout);
  }, [fullText]);

  useEffect(() => {
    const bc = new BroadcastChannel("modular_sync_channel");
    bc.onmessage = (event) => {
      // Ignorar mensajes de la propia pestaña
      if (event.data.senderId === TAB_ID) return;

      if (
        event.data.type === "RAD1_MIRROR_TEXT" &&
        event.data.text !== undefined
      ) {
        if (document.activeElement !== textareaRef.current) {
          setFullText(event.data.text);
        }
      }
      if (
        event.data.type === "RAD1_SYNC_RESULT" &&
        event.data.result !== undefined
      ) {
        setGeneratedResult(event.data.result);
        setIsGenerating(false);
        setIsProcessTimerRunning(false);
      }
      if (
        event.data.type === "RAD1_REPLACE" &&
        event.data.text !== undefined
      ) {
        setFullText(event.data.text);
        setGeneratedResult(null);
        setProcessSeconds(0);
        setIsProcessTimerRunning(false);
      }
      if (event.data.type === "MODULAR1_RESET_ALL") {
        setFullText("");
        setGeneratedResult(null);
        setSelectedReportId(null);
        setReportSearch("");
        setProcessSeconds(0);
        setIsProcessTimerRunning(false);
      }
      if (event.data.type === "MODULAR1_REMOTE_SELECT" && event.data.reportId) {
        setSelectedReportId(event.data.reportId);
        setReportSearch("");
      }
    };
    return () => bc.close();
  }, [handleTextChange]);

  const handleReset = async () => {
    topAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const userId = auth.currentUser?.uid || 'guest_dev_offline';
    setLocalFullText("");
    setFullText("");
    setGeneratedResult(null);
    setSectionHistories({ findings: [], conclusion: [], differential: [] });
    setSectionIndices({ findings: -1, conclusion: -1, differential: -1 });
    setSelectedReportId(null);
    setReportSearch("");
    setProcessSeconds(0);
    setIsProcessTimerRunning(false);
    totalDictationSecondsRef.current = 0;
    studySessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    
    setRawAuditResult(null);
    setIsRawAuditing(false);
    setIsApplyingAudit(false);
    setInlineAuditResult(null);
    setIsInlineAuditing(false);
    setIsIntegratingAudit(false);
    
    // Limpiar sesión en Firebase
    await clearModuleSessions(userId, 'modular1');

    localStorage.removeItem("modular1_last_result");

    const bc = new BroadcastChannel("modular_sync_channel");
    bc.postMessage({ type: "MODULAR1_RESET_ALL", senderId: TAB_ID });
    bc.close();
  };

  const [isLoadingMonthlyAvg, setIsLoadingMonthlyAvg] = useState(false);
  const [monthlyAvgRequested, setMonthlyAvgRequested] = useState(false);

  const loadMonthlyTimingAverage = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || userId === 'guest_dev_offline') return;
    setIsLoadingMonthlyAvg(true);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    try {
      const stats = await fetchUserRealStats(userId, startOfMonth);
      const sessions = collapseGenerationEventsBySession(stats.roiEvents || []);
      if (sessions.length === 0) {
        setMonthlyAvgTiming(null);
      } else {
        const totalDictation = sessions.reduce((sum, s) => sum + s.dictationSeconds, 0);
        const totalFusion = sessions.reduce((sum, s) => sum + s.fusionSeconds, 0);
        setMonthlyAvgTiming({
          avgDictation: totalDictation / sessions.length,
          avgFusion: totalFusion / sessions.length,
          count: sessions.length
        });
      }
      setMonthlyAvgRequested(true);
    } catch (e) {
      console.error("Error loading monthly timing average:", e);
    } finally {
      setIsLoadingMonthlyAvg(false);
    }
  }, []);

  const pushGenerationSnapshot = useCallback((resultSnapshot: any) => {
    const userId = auth.currentUser?.uid || 'guest_dev_offline';
    const snapshot = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
      selectedReportId: selectedReportId,
      fullText: fullText,
      generatedResult: resultSnapshot,
      includeTitle: includeTitle,
      fusionMode: fusionMode,
      flexibleMode: flexibleMode
    };
    setGenerationHistory(prev => {
      const next = [...prev, snapshot].slice(-15);
      setGenerationIndex(next.length - 1);
      return next;
    });
    saveModular1GenerationSnapshot(userId, snapshot);
  }, [selectedReportId, fullText, includeTitle, fusionMode, flexibleMode]);

  const navigateGenerationHistory = useCallback((direction: 'back' | 'forward') => {
    const newIndex = direction === 'back' ? generationIndex - 1 : generationIndex + 1;
    if (newIndex < 0 || newIndex >= generationHistory.length) return;
    const snap = generationHistory[newIndex];
    if (!snap) return;
    setSelectedReportId(snap.selectedReportId);
    setFullText(snap.fullText);
    setLocalFullText(snap.fullText);
    setGeneratedResult(snap.generatedResult);
    setIncludeTitle(snap.includeTitle);
    setFusionMode(snap.fusionMode);
    setFlexibleMode(snap.flexibleMode);
    setGenerationIndex(newIndex);
  }, [generationHistory, generationIndex]);

  const handleGenerateReport = async () => {
    if (!fullText.trim() || isGenerating || isRemoteLocked) return;
    setIsGenerating(true);
    setIsProcessTimerRunning(true);
    if (processSeconds === 0 || generatedResult) setProcessSeconds(0);

    try {
      const baseReport = reports.find((r) => r.id === selectedReportId);
      const fusionStartTime = Date.now();
      const result = await generateStructuredSections(
        fullText,
        baseReport ? baseReport.content : null,
        outputLang,
        fusionMode,
        flexibleMode,
        includeTitle
      );
      const fusionSeconds = Math.round((Date.now() - fusionStartTime) / 1000);
      setGeneratedResult(result);
      setErrorStatus(null);
      
      const userId = auth.currentUser?.uid || 'guest_dev_offline';

      // Limpiar sesión previa
      await clearModuleSessions(userId, 'modular1');

      // Inicializar historiales por sección
      const initialHist: Record<string, string[]> = {
        findings: [result.findings],
        conclusion: [result.conclusion],
        differential: result.differential ? [result.differential] : []
      };
      setSectionHistories(initialHist);
      setSectionIndices({
        findings: 0,
        conclusion: 0,
        differential: result.differential ? 0 : -1
      });

      // Guardar en Firebase para persistencia de sesión
      if (result.findings) await saveModuleSectionHistory(userId, 'modular1', 'findings', result.findings);
      if (result.conclusion) await saveModuleSectionHistory(userId, 'modular1', 'conclusion', result.conclusion);
      if (result.differential) await saveModuleSectionHistory(userId, 'modular1', 'differential', result.differential);

      pushGenerationSnapshot(result);

      // Registrar evento de ROI
      const title = baseReport?.title || fullText.substring(0, 30);
      const complexity = title.toUpperCase().includes('RM') || title.toUpperCase().includes('RESONANCIA') ? 'PLUS' : 
                         (title.toUpperCase().includes('TC') || title.toUpperCase().includes('TOMOGRAFIA') ? 'COMPLEX' : 'SIMPLE');
      
      const dictationSecondsForThisReport = totalDictationSecondsRef.current;
      setLastGenerationTiming({ dictationSeconds: dictationSecondsForThisReport, fusionSeconds });
      recordROIEvent(userId, ROIEventType.GENERATION, complexity, {
        dictationSeconds: dictationSecondsForThisReport,
        fusionSeconds,
        sessionId: studySessionIdRef.current
      });
      totalDictationSecondsRef.current = 0;

      const bc = new BroadcastChannel("modular_sync_channel");
      bc.postMessage({ type: "RAD1_SYNC_RESULT", result, senderId: TAB_ID });
      bc.close();
    } catch (e: any) {
      console.error("Generation Error:", e);
      setErrorStatus("Error de conexión con IA. Reintentando limpieza de sesión...");
      // Soft reset automático de estados de carga
      setIsGenerating(false);
      setIsProcessTimerRunning(false);
    } finally {
      setIsGenerating(false);
      setIsProcessTimerRunning(false);
    }
  };

  useEffect(() => {
    handleGenerateReportRef.current = handleGenerateReport;
  }, [handleGenerateReport]);

  const handleInlineAudit = async () => {
    if (!generatedResult || !generatedResult.findings || isInlineAuditing) return;
    setIsInlineAuditing(true);
    setInlineAuditResult(null);
    try {
      const { rateRadiologyReport } = await import("../services/geminiService");
      const result = await rateRadiologyReport(generatedResult.findings);
      setInlineAuditResult(result);
    } catch (err) {
      console.error("Error in inline audit:", err);
      alert("Error al realizar la auditoría. Por favor reintente.");
    } finally {
      setIsInlineAuditing(false);
    }
  };

  const handleIntegrateInlineAudit = async () => {
    if (!generatedResult || !inlineAuditResult || isIntegratingAudit) return;
    setIsIntegratingAudit(true);
    try {
      const { integrateAuditSuggestions } = await import("../services/geminiService");
      const integrated = await integrateAuditSuggestions(
        generatedResult.findings,
        generatedResult.conclusion,
        inlineAuditResult.recommendations
      );
      
      const userId = auth.currentUser?.uid || 'guest_dev_offline';
      
      // Update local section histories
      setSectionHistories(prev => {
        const newHist = { ...prev };
        newHist.findings = [...(newHist.findings || []), integrated.findings];
        newHist.conclusion = [...(newHist.conclusion || []), integrated.conclusion];
        return newHist;
      });
      
      setSectionIndices(prev => {
        const currentFindingsHist = sectionHistories.findings || [];
        const currentConclusionHist = sectionHistories.conclusion || [];
        return {
          ...prev,
          findings: currentFindingsHist.length,
          conclusion: currentConclusionHist.length
        };
      });
      
      const newResult = {
        ...generatedResult,
        findings: integrated.findings,
        conclusion: integrated.conclusion
      };
      setGeneratedResult(newResult);
      
      // Guardar versión en Firebase
      await saveModuleSectionHistory(userId, 'modular1', 'findings', integrated.findings);
      await saveModuleSectionHistory(userId, 'modular1', 'conclusion', integrated.conclusion);
      
      // Registrar evento de ROI
      recordROIEvent(userId, ROIEventType.RECONCILIATION, 'SIMPLE');

      // Limpiar resultado de la auditoría tras integrar
      setInlineAuditResult(null);
    } catch (err) {
      console.error("Error integrating audit:", err);
      alert("Error al integrar las sugerencias de la auditoría. Por favor reintente.");
    } finally {
      setIsIntegratingAudit(false);
    }
  };

  const handleRawAudit = async () => {
    if (!fullText.trim() || isRawAuditing) return;
    setIsRawAuditing(true);
    try {
      const { rateRadiologyReport } = await import("../services/geminiService");
      const result = await rateRadiologyReport(fullText, 'phd');
      setRawAuditResult(result);
      
      const userId = auth.currentUser?.uid || 'guest_dev_offline';
      await recordDraftingUsage(userId, 'audit');
    } catch (err) {
      console.error("Error auditing raw text:", err);
      alert("Error al realizar la auditoría. Por favor reintente.");
    } finally {
      setIsRawAuditing(false);
    }
  };

  const handleApplyAuditSuggestions = async () => {
    if (!fullText.trim() || !rawAuditResult || isApplyingAudit) return;
    setIsApplyingAudit(true);
    setIsProcessTimerRunning(true);
    setProcessSeconds(0);

    const startTime = Date.now();
    const intervalId = setInterval(() => {
      setProcessSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const { processAIDrafting } = await import("../services/geminiService");
      const result = await processAIDrafting(
        fullText,
        rawAuditResult.recommendations,
        { technique: false, findings: true, conclusion: true, mode: 'structured', applySuggestions: true },
        'phd'
      );

      const userId = auth.currentUser?.uid || 'guest_dev_offline';

      const newResult = {
        technique: generatedResult?.technique || result.technique || "",
        findings: result.findings || generatedResult?.findings || "",
        conclusion: result.conclusion || generatedResult?.conclusion || "",
        differential: generatedResult?.differential || ""
      };

      setGeneratedResult(newResult);
      localStorage.setItem("modular1_last_result", JSON.stringify(newResult));
      setErrorStatus(null);

      // Actualizar historiales de sección para findings y conclusion
      const currentFindingsHist = sectionHistories.findings || [];
      const currentConclusionHist = sectionHistories.conclusion || [];

      setSectionHistories(prev => ({
        ...prev,
        findings: result.findings ? [...currentFindingsHist, result.findings] : currentFindingsHist,
        conclusion: result.conclusion ? [...currentConclusionHist, result.conclusion] : currentConclusionHist
      }));

      setSectionIndices(prev => ({
        ...prev,
        findings: result.findings ? currentFindingsHist.length : prev.findings,
        conclusion: result.conclusion ? currentConclusionHist.length : prev.conclusion
      }));

      if (result.findings) await saveModuleSectionHistory(userId, 'modular1', 'findings', result.findings);
      if (result.conclusion) await saveModuleSectionHistory(userId, 'modular1', 'conclusion', result.conclusion);
      await recordDraftingUsage(userId, 'apply');

      pushGenerationSnapshot(newResult);

      const bc = new BroadcastChannel("modular_sync_channel");
      bc.postMessage({ type: "RAD1_SYNC_RESULT", result: newResult, senderId: TAB_ID });
      bc.close();
    } catch (err) {
      console.error("Error applying audit suggestions:", err);
      alert("Error al aplicar las sugerencias de la auditoría. Por favor reintente.");
    } finally {
      clearInterval(intervalId);
      setIsProcessTimerRunning(false);
      setIsApplyingAudit(false);
    }
  };

  const handleGenerateConclusionOnly = async () => {
    if (!fullText.trim() || isGeneratingConclusion) return;
    setIsGeneratingConclusion(true);
    setIsProcessTimerRunning(true);
    setProcessSeconds(0);

    const startTime = Date.now();
    const intervalId = setInterval(() => {
      setProcessSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const { processAIDrafting } = await import("../services/geminiService");
      const result = await processAIDrafting(
        fullText,
        '',
        { technique: false, findings: false, conclusion: true, mode: 'structured' },
        'phd'
      );

      const userId = auth.currentUser?.uid || 'guest_dev_offline';

      // Preservar estrictamente findings y technique previos
      const newResult = {
        technique: generatedResult?.technique || "",
        findings: generatedResult?.findings || "",
        conclusion: result.conclusion || "",
        differential: generatedResult?.differential || ""
      };

      setGeneratedResult(newResult);
      localStorage.setItem("modular1_last_result", JSON.stringify(newResult));
      setErrorStatus(null);

      // Actualizar SOLO el historial de conclusion
      const currentConclusionHist = sectionHistories.conclusion || [];
      setSectionHistories(prev => ({
        ...prev,
        conclusion: result.conclusion ? [...currentConclusionHist, result.conclusion] : currentConclusionHist
      }));

      setSectionIndices(prev => ({
        ...prev,
        conclusion: result.conclusion ? currentConclusionHist.length : prev.conclusion
      }));

      if (result.conclusion) await saveModuleSectionHistory(userId, 'modular1', 'conclusion', result.conclusion);
      await recordDraftingUsage(userId, 'apply');

      pushGenerationSnapshot(newResult);

      const bc = new BroadcastChannel("modular_sync_channel");
      bc.postMessage({ type: "RAD1_SYNC_RESULT", result: newResult, senderId: TAB_ID });
      bc.close();
    } catch (err) {
      console.error("Error generating conclusion:", err);
      alert("Error al generar la Conclusión. Por favor reintente.");
    } finally {
      clearInterval(intervalId);
      setIsProcessTimerRunning(false);
      setIsGeneratingConclusion(false);
    }
  };

  const handlePostProcess = async (
    section: "findings" | "conclusion" | "differential",
    action: "optimize" | "pro" | "translate" | "structure",
    lang?: string,
  ) => {
    if (!generatedResult || isPostProcessing) return;
    setIsPostProcessing(section);
    try {
      const textToProcess = generatedResult[section];
      if (!textToProcess) return;

      const result = await postProcessReport(
        textToProcess,
        action,
        lang || outputLang,
      );
      
      const userId = auth.currentUser?.uid || 'guest_dev_offline';

      // Actualización local inmediata para respuesta instantánea en UI
      setSectionHistories(prev => {
        const newHist = { ...prev };
        newHist[section] = [...(newHist[section] || []), result];
        return newHist;
      });

      setSectionIndices(prev => {
        const currentHist = sectionHistories[section] || [];
        return { 
          ...prev, 
          [section]: currentHist.length 
        };
      });

      const newResult = { ...generatedResult, [section]: result };
      setGeneratedResult(newResult);

      // Guardar versión en Firebase
      await saveModuleSectionHistory(userId, 'modular1', section, result);

      // Registrar evento de ROI
      let eventType = ROIEventType.IMPROVEMENT;
      if (action === 'translate') eventType = ROIEventType.TRANSLATION;
      else if (action === 'structure') eventType = ROIEventType.STRUCTURE;
      else if (action === 'narrative') eventType = ROIEventType.NARRATIVE;
      else if (action === 'optimize') eventType = ROIEventType.OPTIMIZE;
      
      await recordROIEvent(userId, eventType);

      const bc = new BroadcastChannel("modular_sync_channel");
      bc.postMessage({ type: "RAD1_SYNC_RESULT", result: newResult, senderId: TAB_ID });
      bc.close();
    } catch (e: any) {
      setErrorStatus("Error al optimizar sección. Reintente en unos segundos.");
      console.error("PostProcess Error:", e);
    } finally {
      setIsPostProcessing(null);
    }
  };

  const toggleDarkMode = () => {
    if (!isWide) {
      const order: ('day' | 'mixed' | 'dark')[] = ['day', 'mixed', 'dark'];
      const currentIndex = order.indexOf(globalThemeState);
      const next = order[(currentIndex + 1) % order.length];
      Object.keys(localStorage).forEach(key => { if (key.startsWith('module_theme_')) localStorage.removeItem(key); });
      localStorage.setItem('app_theme', next);
      setGlobalThemeState(next);
      setIsDarkMode(next === 'dark');
      window.dispatchEvent(new Event('app_theme_changed'));
      return;
    }
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("module_theme_modular1", next ? "dark" : "day");
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  useEffect(() => {
    localStorage.setItem("modular1_engine_mode", engineMode);
  }, [engineMode]);

  const handleToggleRecording = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      if (isRemoteLocked) return;
      await startRecording();
    }
  };

  const startRecording = async () => {
    if (isProcessingAudio || isRecording || isRemoteLocked) return;

    if (anchorMode) {
      anchorPosRef.current = lastCursorPosRef.current;
    }

    // Notificar bloqueo a otras instancias
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'RECORDING_START', senderId: TAB_ID });
    bc.close();

    setLiveStreamText("");
    lastLiveTextRef.current = "";

    setIsRecording(true);
    recordStartTimeRef.current = Date.now();
    continuousRecordingSecondsRef.current = 0;

    // Iniciamos cronómetro de proceso solicitado
    setIsProcessTimerRunning(true);
    if (processSeconds === 0 || generatedResult) setProcessSeconds(0);

    try {
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.start();
    } catch (e) {
      console.error("Start Recording Error:", e);
      setIsRecording(false);
      setIsProcessTimerRunning(false);
      clearInterval(timerRef.current);
      setErrorStatus("Error de Micrófono: Verifique permisos del navegador.");
    }
  };

  const stopRecording = async () => {
    if (!isRecording) return;

    setIsRecording(false);
    setIsProcessingAudio(true);

    // Notificar liberación de instancia
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'RECORDING_STOP', senderId: TAB_ID });
    bc.close();

    try {
      let finalTranscription = "";
      const profile = localStorage.getItem("modular_voice_profile");

      if (recorderRef.current) {
        const { blob, mimeType } = await recorderRef.current.stop();
          const base64 = await blobToBase64(blob);
          
          // PERSISTENCIA PRE-ENVÍO: Guardamos el audio por si falla la red o el socket
          const backupId = `mod1_${Date.now()}`;
          await saveTranscriptionBackup({
            id: backupId,
            blobBase64: base64,
            mimeType,
            timestamp: Date.now(),
            module: 'modular1',
            engine: engineMode,
            durationSeconds: Math.round((Date.now() - recordStartTimeRef.current) / 1000)
          });

            try {
              finalTranscription = await transcribeAudio(blob, mimeType, engineMode);
            
              // SI ÉXITO: Eliminamos el backup
              await deleteTranscriptionBackup(backupId);
            } catch (error) {
               console.error("Transcription execution error:", error);
               // MARCAR COMO ERROR PARA MOSTRAR INMEDIATAMENTE EL MECANISMO DE RESCATE
               await saveTranscriptionBackup({
                 id: backupId,
                 blobBase64: base64,
                 mimeType,
                 timestamp: Date.now(),
                 module: 'modular1',
                 engine: engineMode,
                 isError: true,
                 durationSeconds: Math.round((Date.now() - recordStartTimeRef.current) / 1000)
               });
               throw error;
            }
        }

      if (finalTranscription && finalTranscription.trim().length > 1) {
        // Si estamos en móvil, enviamos a la nube para el "Modo Espejo"
        if (isMobileDevice() && auth.currentUser) {
          streamTextToCloud(auth.currentUser.uid, finalTranscription, 'final');
        }

        if (anchorMode) {
          const baseText = localFullText || fullText || "";
          const pos = Math.min(Math.max(anchorPosRef.current, 0), baseText.length);
          const before = baseText.substring(0, pos);
          const after = baseText.substring(pos);
          const insertion = buildAnchorInsertion(before, finalTranscription, after);
          const newVal = before + insertion + after;
          const newCursorPos = before.length + insertion.length;
          anchorPosRef.current = newCursorPos;
          handleTextChange(newVal);
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
          }, 50);
        } else {
          const newVal =
            !fullText || fullText.trim() === ""
              ? finalTranscription
              : fullText + "\n" + finalTranscription;
          handleTextChange(newVal);
        }
      } else if (!finalTranscription) {
        // Si no se obtuvo transcripción pero no hubo error explícito lanzado
        console.warn("No transcription obtained from audio source.");
      }
    } catch (e: any) {
      console.error("Stop Recording Error:", e);
      const isNetworkError = e.message?.includes("fetch") || e.message?.includes("network") || e.message?.includes("socket");
      setErrorStatus(isNetworkError ? "Error de red al procesar audio. Por favor, reintente." : "No se pudo procesar el audio.");
      // Solo alert si no es un error de estado manejado
      if (!isNetworkError) {
        alert("No se ha podido procesar el dictado. Por favor, intente de nuevo.");
      }
    } finally {
      setIsProcessingAudio(false);
      setIsProcessTimerRunning(false);
      setLiveStreamText("");
    }
  };

  const handleSelectFrequentReport = (id: string) => {
    setSelectedReportId(id);
    setReportSearch("");
    if (auth.currentUser) incrementReportUsage(auth.currentUser.uid, id);
  };

  const toggleFavorite = async (id: string, current: boolean) => {
    if (!auth.currentUser) return;
    try {
      // Optimizamos UI localmente primero para feedback instantáneo si fuera necesario, 
      // pero aquí dependemos de la suscripción onSnapshot de reports.
      await toggleFavoriteReportCloud(id, current, auth.currentUser.uid);
    } catch (e) {
      console.error("Error toggling favorite:", e);
    }
  };

  const selectedReport = reports.find((r) => r.id === selectedReportId);
  const matchesActiveCategory = (r: ReportTemplate) => {
    if (!activeCategoryFilterId) return true;
    const cat = mod1ReportCategories.find((c) => c.id === activeCategoryFilterId);
    return cat ? cat.reportIds.includes(r.id) : true;
  };

  const filteredReports = useMemo(() => {
    if (!reportSearch.trim()) return [];
    
    // Normalización para ignorar acentos y diacríticos
    const normalize = (str: string) => 
      str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      
    const searchTerms = normalize(reportSearch).split(/\s+/).filter(t => t.length > 0);
    
    return reports.filter((r) => {
      if (r.deletedAt) return false;
      if (!matchesActiveCategory(r)) return false;
      const target = normalize(r.title);
      return searchTerms.every(term => target.includes(term));
    });
  }, [reports, reportSearch, mod1ReportCategories, activeCategoryFilterId]);

  // Lógica de informes frecuentes
  const frequentReports = useMemo(() => {
    return [...reports]
      .filter((r) => !r.deletedAt && matchesActiveCategory(r))
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .slice(0, activeCategoryFilterId ? 50 : 5);
  }, [reports, mod1ReportCategories, activeCategoryFilterId]);

  const favoriteReports = useMemo(() => {
    return [...reports]
      .filter((r) => !r.deletedAt && mod1Favorites.includes(r.id) && matchesActiveCategory(r))
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
  }, [reports, mod1Favorites, mod1ReportCategories, activeCategoryFilterId]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const renderFrequentReportsList = () => {
    const editingCategory = categoryEditModeId ? mod1ReportCategories.find((c) => c.id === categoryEditModeId) : null;
    const list = editingCategory
      ? (() => {
          const q = categoryEditSearch.trim();
          if (!q) return reports.filter((r) => !r.deletedAt).slice(0, 50);
          const normalize = (str: string) => str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
          const terms = normalize(q).split(/\s+/).filter((t) => t.length > 0);
          return reports.filter((r) => !r.deletedAt && terms.every((t) => normalize(r.title).includes(t)));
        })()
      : (showFavoritesOnly ? favoriteReports : frequentReports);
    
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-2 ml-1">
          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
            {editingCategory ? `Editando "${editingCategory.name}"` : (showFavoritesOnly ? 'Tus Favoritos' : 'Más frecuentes')}
          </p>
          {editingCategory ? (
            <button
              onClick={() => setCategoryEditModeId(null)}
              className="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase bg-emerald-500 text-white shadow-sm"
            >
              Listo ({editingCategory.reportIds.length})
            </button>
          ) : (
            <button 
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`p-1 rounded-lg transition-all ${showFavoritesOnly ? 'bg-amber-500 text-white shadow-md ring-1 ring-amber-600' : 'text-gray-300 hover:text-amber-500 opacity-60 hover:opacity-100'}`}
              title={showFavoritesOnly ? "Ver frecuentes" : "Ver favoritos MODULAR-1"}
            >
              <Heart size={12} fill={showFavoritesOnly ? "currentColor" : "none"} />
            </button>
          )}
        </div>

        {editingCategory && (
          <div className="relative mb-2">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={categoryEditSearch}
              onChange={(e) => setCategoryEditSearch(e.target.value)}
              placeholder="Buscar informe para añadir..."
              autoFocus
              className={`w-full pl-7 pr-2 py-1.5 text-[10px] rounded-lg border outline-none ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white" : "bg-white border-gray-200 text-gray-800"}`}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {list.length === 0 && showFavoritesOnly && !editingCategory && (
            <p className="text-[8px] font-medium text-gray-400 italic py-2">No tienes informes marcados como favoritos.</p>
          )}
          {list.map((r) => {
            const isChecked = editingCategory ? editingCategory.reportIds.includes(r.id) : false;
            return (
              <button
                key={r.id}
                onClick={() => editingCategory ? handleToggleReportInCategory(r.id) : handleSelectFrequentReport(r.id)}
                className={`px-3 py-1.5 rounded-lg border text-[9px] font-black uppercase tracking-tight transition-all active:scale-95 flex items-center gap-1 ${
                  editingCategory
                    ? (isChecked ? "bg-emerald-600 border-emerald-600 text-white shadow-md" : isDarkMode ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-white border-gray-100 text-gray-500")
                    : (selectedReportId === r.id ? "bg-blue-600 border-blue-600 text-white shadow-md" : isDarkMode ? "bg-gray-800 border-gray-700 text-gray-400 hover:text-blue-400" : "bg-white border-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600")
                }`}
              >
                {editingCategory && isChecked && <Check size={9} />}
                {r.title}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      resolve-layout="Modular1"
      ref={containerRef}
      className={`flex flex-col h-full transition-colors ${isDarkMode ? "bg-[#1e2024] text-gray-200" : "bg-[#f8fafc] text-gray-800"}`}
    >
      <div
        className={`sticky top-0 px-6 py-4 border-b shadow-sm z-30 shrink-0 flex justify-between items-center ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center space-x-3">
          <div className="bg-[#2563eb] p-2 rounded-xl shadow-md">
            <FileText size={18} className="text-white" />
          </div>
          <h1
            className={`font-black text-[18px] uppercase tracking-tight font-['Outfit'] ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          >
            MODULAR-1
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <button type="button" onClick={() => navigateGenerationHistory('back')} disabled={generationIndex <= 0} title="Informe anterior" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => navigateGenerationHistory('forward')} disabled={generationIndex < 0 || generationIndex >= generationHistory.length - 1} title="Informe siguiente" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronRight size={16} /></button>
          <div className={`h-5 w-[1px] ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} mx-1`} />
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-xl border transition-all ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-yellow-400" : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50"}`}
          >
            {!isWide ? (
              globalThemeState === 'day' ? <Sun size={18} /> : globalThemeState === 'mixed' ? <SunMoon size={18} /> : <Moon size={18} />
            ) : (
              isDarkMode ? <Sun size={18} /> : <Moon size={18} />
            )}
          </button>
          <button
            onClick={handleReset}
            className={`p-2 rounded-xl border transition-colors ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-gray-400" : "bg-white border-gray-200 text-gray-400 hover:text-red-500 hover:bg-gray-50"}`}
          >
            <RotateCcw size={18} />
          </button>
        </div>
      </div>

      <div
        className={`flex-1 flex ${isWide ? "flex-row overflow-hidden" : "flex-col overflow-y-auto"}`}
      >
        <div
          className={`flex-1 flex flex-col p-4 md:p-6 space-y-6 custom-scrollbar ${isWide ? "overflow-y-auto pb-24" : ""}`}
        >
          <div ref={topAnchorRef} />
          {!isWide && (
            <div
              ref={reportSearchContainerRef}
              className={`rounded-[24px] shadow-sm border p-5 transition-all ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-slate-200"}`}
            >
              <label className="text-[10px] font-black text-slate-400 uppercase mb-3 block tracking-widest">
                1. INFORME BASE DE NORMALIDAD
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                />
                <input
                  type="text"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  onKeyDown={handleReportSearchKeyDown}
                  placeholder="Buscar informe base..."
                  className={`w-full pl-10 pr-4 py-3 border rounded-2xl text-sm font-medium outline-none transition-all ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white" : "bg-slate-50 border-slate-100 text-slate-900 focus:bg-white focus:border-blue-400"}`}
                />
                <button
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${showFavoritesOnly ? 'text-amber-500 bg-amber-500/10' : 'text-gray-300 hover:text-amber-500'}`}
                  title="Filtrar Favoritos MODULAR-1"
                >
                   <Heart size={16} fill={showFavoritesOnly ? "currentColor" : "none"} />
                </button>
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto mt-2 pb-1 -mx-1 px-1 custom-scrollbar">
                {mod1ReportCategories.map(cat => {
                  const isActive = activeCategoryFilterId === cat.id;
                  const isEditing = categoryEditModeId === cat.id;
                  return (
                    <div
                      key={cat.id}
                      className={`shrink-0 flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg text-[9px] font-black uppercase tracking-wide border transition-all whitespace-nowrap ${
                        isEditing ? 'bg-emerald-600 border-emerald-600 text-white' : isActive ? 'bg-blue-600 border-blue-600 text-white' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500')
                      }`}
                    >
                      <button type="button" onClick={() => setActiveCategoryFilterId(isActive ? null : cat.id)} className="whitespace-nowrap">
                        {cat.name}
                      </button>
                      <button type="button" onClick={() => setCategoryEditModeId(isEditing ? null : cat.id)} title="Editar informes de esta categoría" className="opacity-70 hover:opacity-100 p-0.5">
                        <Pencil size={8} />
                      </button>
                      <button type="button" onClick={() => handleDeleteCategory(cat.id)} title="Eliminar categoría" className="opacity-70 hover:opacity-100 p-0.5">
                        <X size={9} />
                      </button>
                    </div>
                  );
                })}
                {isAddingCategory ? (
                  <div className="shrink-0 flex items-center gap-1">
                    <input
                      type="text"
                      autoFocus
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateCategory();
                        if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(""); }
                      }}
                      placeholder="Nombre..."
                      className={`w-24 px-2 py-1 rounded-lg border text-[9px] font-bold outline-none ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                    />
                    <button type="button" onClick={handleCreateCategory} className="p-1 rounded-lg bg-emerald-500 text-white"><Check size={10} /></button>
                    <button type="button" onClick={() => { setIsAddingCategory(false); setNewCategoryName(""); }} className="p-1 rounded-lg text-gray-400"><X size={10} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setIsAddingCategory(true)} className={`shrink-0 p-1.5 rounded-lg border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-blue-400' : 'bg-white border-gray-200 text-gray-400 hover:text-blue-600'}`} title="Crear categoría">
                    <Plus size={10} />
                  </button>
                )}
              </div>

                {reportSearch &&
                !selectedReport &&
                filteredReports.length > 0 && (
                  <div
                    className={`mt-2 border rounded-2xl overflow-hidden shadow-xl max-h-[300px] overflow-y-auto custom-scrollbar ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-slate-100"}`}
                  >
                    {filteredReports.slice(0, 30).map((r) => {
                      const isMod1Fav = mod1Favorites.includes(r.id);
                      return (
                        <div key={r.id} className="flex items-center border-b last:border-none group/item">
                          <button
                            onClick={() => {
                              setSelectedReportId(r.id);
                              setReportSearch("");
                            }}
                            className={`flex-1 text-left px-4 py-2.5 text-[10px] font-black uppercase transition-colors ${isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-slate-700 hover:bg-blue-50"}`}
                          >
                            {r.title}
                          </button>
                          <div className="flex items-center pr-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onEditReport?.(r.id); }}
                              title="Editar este informe base"
                              className={`p-1.5 opacity-30 group-hover/item:opacity-100 hover:!opacity-100 transition-all text-gray-400 hover:text-indigo-500 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-indigo-50/50'}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleCopyReportContent(e, r)}
                              title="Copiar contenido del informe"
                              className={`p-1.5 opacity-30 group-hover/item:opacity-100 hover:!opacity-100 transition-all text-gray-400 hover:text-blue-500 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-blue-50/50'}`}
                            >
                              {copiedReportId === r.id ? (
                                <Check size={14} className="text-emerald-500" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                            <button 
                              onClick={(e) => toggleMod1Favorite(e, r.id)}
                              className={`p-1.5 opacity-30 group-hover/item:opacity-100 transition-all rounded-lg ${isMod1Fav ? 'text-amber-500 opacity-100' : 'text-gray-400 hover:text-amber-500'}`}
                            >
                              <Heart size={14} fill={isMod1Fav ? "currentColor" : "none"} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              {selectedReport && (
                <div className={`mt-3 p-3 rounded-xl flex justify-between items-center border animate-in zoom-in-95 ${isDarkMode ? 'bg-blue-900/10 border-blue-900/30' : 'bg-blue-50/50 border-blue-100'}`}>
                  <div className="flex items-center space-x-3 truncate">
                    <div className="bg-blue-600 p-1 rounded-lg text-white">
                      <Check size={12} strokeWidth={4} />
                    </div>
                    <span className={`text-[11px] font-black truncate uppercase tracking-tight ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {selectedReport.title}
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedReportId(null)}
                    className={`p-1 rounded-lg transition-all ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-white'}`}
                  >
                    <X size={14} className="text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              )}

              {!reportSearch && renderFrequentReportsList()}
            </div>
          )}

          {/* DESPLEGABLE DE BÚSQUEDA DE FRASES CLÍNICAS ALMACENADAS */}
          <div ref={phraseSearchContainerRef} className="relative">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setIsPhraseSearchOpen((prev) => !prev);
                  if (isPhraseSearchOpen) setPhraseSearch("");
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border shadow-sm ${
                  isPhraseSearchOpen
                    ? "bg-blue-600 border-blue-600 text-white shadow-blue-500/20"
                    : isDarkMode
                    ? "bg-[#2a2d34] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600"
                    : "bg-white border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200"
                }`}
              >
                <Search size={12} className={isPhraseSearchOpen ? "text-white" : "text-blue-500"} />
                <span>Frases</span>
                {isPhraseSearchOpen ? (
                  <ChevronUp size={12} className="opacity-70" />
                ) : (
                  <ChevronDown size={12} className="opacity-70" />
                )}
              </button>
            </div>

            {isPhraseSearchOpen && (
              <div
                className={`mt-2 p-3.5 rounded-2xl border shadow-xl animate-in slide-in-from-top-2 duration-200 ${
                  isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-slate-200"
                }`}
              >
                <div className="relative mb-2.5">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    value={phraseSearch}
                    onChange={(e) => setPhraseSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsPhraseSearchOpen(false);
                        setPhraseSearch("");
                      }
                    }}
                    placeholder="Escribe al menos 3 letras para buscar..."
                    autoFocus
                    className={`w-full pl-9 pr-8 py-2 text-xs rounded-xl border outline-none font-medium transition-all ${
                      isDarkMode
                        ? "bg-[#2a2d34] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                        : "bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:bg-white"
                    }`}
                  />
                  {phraseSearch && (
                    <button
                      type="button"
                      onClick={() => setPhraseSearch("")}
                      className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 p-0.5 ${isDarkMode ? 'hover:text-gray-200' : 'hover:text-gray-600'}`}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                <div className="max-h-[220px] overflow-y-auto custom-scrollbar">
                  {phraseSearch.trim().length < 3 ? (
                    <div className="py-2.5 px-3 text-center text-[11px] font-medium text-slate-400">
                      Escribe al menos 3 caracteres para ver coincidencias...
                    </div>
                  ) : filteredPhrases.length === 0 ? (
                    <div className="py-2.5 px-3 text-center text-[11px] font-medium text-slate-400">
                      No se encontraron frases coincidentes
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredPhrases.slice(0, 30).map((phrase) => (
                        <button
                          key={phrase.id}
                          type="button"
                          onClick={() => handleCopyPhrase(phrase.text)}
                          title="Copiar frase al portapapeles"
                          className={`w-full text-left p-2.5 rounded-xl text-xs transition-colors group flex items-center justify-between gap-2.5 border border-transparent ${
                            isDarkMode
                              ? "hover:bg-gray-800/80 hover:border-gray-700 text-gray-200"
                              : "hover:bg-blue-50/70 hover:border-blue-100 text-slate-700"
                          }`}
                        >
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <span className="text-blue-500 text-[10px] mt-0.5 font-mono shrink-0">✦</span>
                            <span className="font-medium leading-relaxed break-words">{phrase.text}</span>
                          </div>
                          <span className="shrink-0 text-[10px] text-gray-400 group-hover:text-blue-500 flex items-center gap-1 font-bold uppercase">
                            <Copy size={11} /> Copiar
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-[24px] shadow-sm border p-5 transition-all ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-slate-200"}`}
          >
            <div className="flex justify-between items-center mb-3">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                2. HALLAZGOS PATOLÓGICOS
              </label>
              {Boolean((localFullText || fullText || "").trim()) && (
                <div className="flex items-center gap-3 animate-in fade-in duration-200">
                  <button
                    type="button"
                    onClick={handleCopyHallazgos}
                    title="Copiar hallazgos al portapapeles"
                    className={`text-[9px] font-black uppercase flex items-center gap-1 transition-colors ${
                      copiedHallazgos
                        ? "text-emerald-500 font-bold"
                        : "text-gray-400 hover:text-blue-500"
                    }`}
                  >
                    {copiedHallazgos ? (
                      <Check size={12} className="text-emerald-500" />
                    ) : (
                      <Copy size={12} />
                    )}
                    {copiedHallazgos ? "Copiado!" : "Copiar"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTextChange("")}
                    title="Limpiar hallazgos"
                    className="text-[9px] font-black text-gray-400 hover:text-red-500 uppercase flex items-center gap-1 transition-colors"
                  >
                    <Eraser size={12} /> Limpiar
                  </button>
                </div>
              )}
            </div>
            <div className="relative group">
               {errorStatus && (
                  <div className="absolute -top-10 left-0 right-0 z-50 flex justify-center animate-in slide-in-from-top-2 duration-300">
                    <div className="bg-red-500 text-white text-[9px] font-black px-4 py-2 rounded-full shadow-lg border border-red-400 flex items-center gap-2">
                       <Activity size={12} className="animate-pulse" />
                       <span>{errorStatus}</span>
                    </div>
                  </div>
                )}
              <textarea
                ref={textareaRef}
                value={localFullText}
                onFocus={() => { isTypingRef.current = true; }}
                onBlur={() => { isTypingRef.current = false; }}
                onSelect={(e) => { lastCursorPosRef.current = e.currentTarget.selectionStart; }}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder="Dicta los hallazgos patológicos encontrados..."
                className={`w-full min-h-[160px] p-4 border rounded-[20px] text-[15px] font-medium outline-none resize-y transition-all ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white focus:border-blue-500" : "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 shadow-sm"}`}
              />
              {(isRecording || isProcessingAudio) && (
                <div
                  className={`p-3 rounded-2xl border animate-in fade-in slide-in-from-top-2 shadow-sm ${isDarkMode ? "bg-gray-800/40 border-blue-500/20 text-blue-300" : "bg-blue-50/50 border-blue-100 text-blue-700"}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full bg-red-500 animate-pulse`}></div>
                      <span className="text-[10px] font-black uppercase tracking-widest opacity-60">
                         Transcripción en Vivo
                      </span>
                    </div>
                  </div>

                  {!liveStreamText && isRecording && (
                    <div className="flex items-center justify-center gap-2 py-1.5">
                       <div className="flex items-center space-x-1">
                          {[1, 2, 3].map(i => (
                             <div key={i} className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }}></div>
                          ))}
                       </div>
                       <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Escuchando audio...</p>
                    </div>
                  )}

                  {!liveStreamText && isProcessingAudio && (
                    <div className="flex items-center justify-center gap-2 py-1.5">
                       <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                       <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-500">Recuperando dictado...</p>
                    </div>
                  )}

                  {liveStreamText && (
                    <p className="text-[13px] font-medium leading-relaxed italic">
                      {liveStreamText}
                      <span className="inline-block w-1 h-3 ml-1 bg-blue-500 animate-pulse"></span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col items-center py-6 space-y-3">
              <button
                onClick={handleToggleRecording}
                disabled={isProcessingAudio || isRemoteLocked}
                className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all active:scale-95 ${isRecording ? "bg-red-500 animate-pulse ring-8 ring-red-500/10" : isRemoteLocked ? "bg-gray-400 cursor-not-allowed" : "bg-[#2563eb] hover:bg-blue-700 ring-8 ring-blue-500/5"}`}
              >
                {isProcessingAudio ? (
                  <div className="w-8 h-8 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                ) : isRecording ? (
                  <Square size={28} className="text-white fill-current" />
                ) : isRemoteLocked ? (
                  <Activity size={28} className="text-white/50" />
                ) : (
                  <Mic size={38} className="text-white" />
                )}
              </button>

              {/* Cronómetro de proceso centralizado */}
              {(isRecording ||
                isProcessTimerRunning ||
                isGenerating ||
                isProcessingAudio) && (
                <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                  <div className="flex items-center space-x-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 shadow-sm">
                    <Timer size={14} className="text-blue-500 animate-pulse" />
                    <span className={`text-sm font-mono font-black tracking-wider ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>

                      {formatTime(processSeconds)}
                    </span>
                  </div>
                  <span className="text-[8px] font-black text-blue-400 uppercase tracking-[0.2em] mt-1.5">
                    {isRecording
                      ? "Escuchando..."
                      : isProcessingAudio
                        ? "Transcribiendo..."
                        : isGenerating
                          ? "Generando Informe..."
                          : isRemoteLocked
                          ? "Grabación en curso (Otra Ventana)"
                          : "Procesando..."}
                  </span>
                </div>
              )}

              {!isRecording &&
                !isProcessTimerRunning &&
                !isGenerating &&
                !isProcessingAudio && (
                  <div className="flex flex-col items-center space-y-3">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                      DICTAR HALLAZGOS
                    </span>
                    <div
                      className={`flex rounded-lg p-0.5 border shadow-inner ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-slate-100"}`}
                    >
                      <button
                        onClick={() => setEngineMode("phd")}
                        className={`px-3 py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "phd" ? "bg-blue-600 text-white shadow-md" : "text-gray-400 hover:text-gray-500"}`}
                      >
                        <GraduationCap size={12} className="mr-1" /> PHD
                      </button>
                      <button
                        onClick={() => setEngineMode("gm1")}
                        className={`px-3 py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "gm1" ? "bg-indigo-600 text-white shadow-md" : "text-gray-400 hover:text-gray-500"}`}
                      >
                        <Activity size={12} className="mr-1" /> GM1
                      </button>
                    </div>
                  </div>
                )}
            </div>
            <div className={`pt-5 border-t ${isDarkMode ? 'border-gray-800' : 'border-slate-100'}`}>
              <button
                onClick={handleGenerateReport}
                disabled={!fullText || isGenerating}
                className={`w-full py-3.5 rounded-xl text-[12px] font-black uppercase tracking-[0.1em] shadow-xl transition-all active:scale-[0.98] flex justify-center items-center gap-2 ${isGenerating ? "bg-blue-400 cursor-not-allowed" : "bg-[#2563eb] text-white hover:bg-blue-700 shadow-blue-600/20"}`}
              >
                {isGenerating ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Sparkles size={16} fill="white" /> GENERAR INFORME FINAL
                  </>
                )}
              </button>
            </div>

            {/* Barra de Herramientas y Parámetros distribuida ordenadamente desde la izquierda */}
            <div className={`mt-3 pt-3 border-t flex flex-wrap items-center justify-start gap-y-2 gap-x-2.5 sm:gap-x-3 w-full ${isDarkMode ? 'border-gray-800' : 'border-slate-100'}`}>
              {/* Grupo 1: Acciones de Análisis y Síntesis */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {/* Botón 1: Auditar */}
                <button
                  type="button"
                  disabled={!fullText.trim() || isRawAuditing || isApplyingAudit}
                  onClick={handleRawAudit}
                  className={`px-2 sm:px-2.5 py-1 rounded-xl border flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-black tracking-tight whitespace-nowrap transition-all shadow-sm ${
                    !fullText.trim() || isRawAuditing || isApplyingAudit
                      ? 'opacity-40 cursor-not-allowed border-transparent bg-transparent'
                      : isDarkMode
                        ? 'bg-[#1e2024] border-gray-700/80 hover:border-indigo-500/60 hover:bg-slate-800 text-gray-200 active:scale-[0.98]'
                        : 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 text-slate-800 active:scale-[0.98]'
                  }`}
                >
                  <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {isRawAuditing ? <RotateCw size={11} className="animate-spin sm:w-[13px] sm:h-[13px]" /> : <ShieldCheck size={11} className="sm:w-[13px] sm:h-[13px]" />}
                  </div>
                  <span>{isRawAuditing ? "Auditando..." : "Auditar"}</span>
                </button>

                {/* Botón 2: Conclusión con signo de interrogación aclaratorio al lado */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!fullText.trim() || isGeneratingConclusion}
                    onClick={handleGenerateConclusionOnly}
                    className={`px-2 sm:px-2.5 py-1 rounded-xl border flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-black tracking-tight whitespace-nowrap transition-all shadow-sm ${
                      !fullText.trim() || isGeneratingConclusion
                        ? 'opacity-40 cursor-not-allowed border-transparent bg-transparent'
                        : isDarkMode
                          ? 'bg-[#1e2024] border-gray-700/80 hover:border-blue-500/60 hover:bg-slate-800 text-gray-200 active:scale-[0.98]'
                          : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 text-slate-800 active:scale-[0.98]'
                    }`}
                  >
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {isGeneratingConclusion ? <RotateCw size={11} className="animate-spin sm:w-[13px] sm:h-[13px]" /> : <FileText size={11} className="sm:w-[13px] sm:h-[13px]" />}
                    </div>
                    <span>{isGeneratingConclusion ? "Generando..." : "Conclusión"}</span>
                  </button>

                  <HelpTooltip 
                    text="Síntesis de los hallazgos ya dictados por el radiólogo, sin análisis diagnóstico adicional ni diferenciales."
                    isDarkMode={isDarkMode}
                    iconSize={13}
                  />
                </div>
              </div>

              {/* Separador sutil cuando están en la misma fila */}
              <div className={`hidden sm:block w-px h-4 shrink-0 ${isDarkMode ? 'bg-gray-700/80' : 'bg-slate-200'}`}></div>

              {/* Grupo 2: Toggle Título y Parámetros del Motor de Fusión */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-wrap">
                {/* Botón Toggle Anclaje (compacto: solo icono) */}
                <button
                  type="button"
                  onClick={() => setAnchorMode(!anchorMode)}
                  className={`p-1 sm:p-1.5 rounded-xl border transition-all shadow-sm active:scale-[0.98] flex items-center justify-center ${
                    anchorMode
                      ? (isDarkMode ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'bg-blue-50 border-blue-300 text-blue-600')
                      : (isDarkMode ? 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300' : 'bg-transparent border-slate-200 text-slate-400 hover:text-slate-600')
                  }`}
                  title={anchorMode ? "Anclaje activo: el nuevo dictado se inserta en la posición del cursor" : "Anclaje desactivado: el dictado se agrega al final"}
                >
                  <Anchor size={13} className={anchorMode ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : 'text-current'} />
                </button>

                {/* Botón Toggle Título */}
                <button
                  type="button"
                  onClick={() => setIncludeTitle(!includeTitle)}
                  className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl border text-[10px] sm:text-xs font-bold tracking-tight whitespace-nowrap transition-all shadow-sm active:scale-[0.98] ${
                    includeTitle
                      ? (isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500')
                      : (isDarkMode ? 'bg-transparent border-gray-700 text-gray-500 hover:text-gray-300' : 'bg-transparent border-slate-200 text-slate-400 hover:text-slate-600')
                  }`}
                  title={includeTitle ? "Título activo en el informe" : "Título desactivado"}
                >
                  {includeTitle ? "Titulo On" : "Titulo Off"}
                </button>

                <div className={`w-px h-3.5 shrink-0 ${isDarkMode ? 'bg-gray-700/70' : 'bg-slate-200'}`}></div>

                <div className="flex items-center gap-1.5 sm:gap-2.5 select-none">
                  {/* Selector Fast / Normal */}
                  <div className="flex items-center gap-1 sm:gap-2">
                    <label className="flex items-center gap-1 sm:gap-1.5 cursor-pointer group">
                      <div 
                        onClick={() => setFusionMode('fast')}
                        className={`w-3 h-3 rounded-full border flex items-center justify-center transition-all ${fusionMode === 'fast' ? 'border-blue-500 bg-blue-500' : (isDarkMode ? 'border-gray-600' : 'border-slate-300')}`}
                      >
                        {fusionMode === 'fast' && <div className="w-1 h-1 rounded-full bg-white"></div>}
                      </div>
                      <span className={`text-[8.5px] font-black tracking-wider uppercase ${fusionMode === 'fast' ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : 'text-slate-400'}`}>Fast</span>
                    </label>
                    <label className="flex items-center gap-1 sm:gap-1.5 cursor-pointer group">
                      <div 
                        onClick={() => setFusionMode('normal')}
                        className={`w-3 h-3 rounded-full border flex items-center justify-center transition-all ${fusionMode === 'normal' ? 'border-blue-500 bg-blue-500' : (isDarkMode ? 'border-gray-600' : 'border-slate-300')}`}
                      >
                        {fusionMode === 'normal' && <div className="w-1 h-1 rounded-full bg-white"></div>}
                      </div>
                      <span className={`text-[8.5px] font-black tracking-wider uppercase ${fusionMode === 'normal' ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : 'text-slate-400'}`}>Normal</span>
                    </label>
                  </div>

                  {/* Switch Flexible */}
                  <div className="flex items-center gap-1 sm:gap-1.5 cursor-pointer select-none" onClick={() => setFlexibleMode(!flexibleMode)}>
                    <button
                      type="button"
                      className={`relative w-6 h-3.5 rounded-full transition-colors duration-200 outline-none focus:outline-none ${flexibleMode ? 'bg-[#2563eb]' : (isDarkMode ? 'bg-gray-600' : 'bg-slate-300')}`}
                      title="Modo Flexible Alternativo"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-2.5 h-2.5 rounded-full bg-white transition-transform duration-200 ${flexibleMode ? 'translate-x-2.5' : ''}`}></span>
                    </button>
                    <span className={`text-[8px] font-black tracking-wider uppercase ${flexibleMode ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : 'text-slate-400'}`}>FLEXIBLE</span>
                  </div>

                  {/* Ayuda de Modos de Fusión */}
                  <button
                    type="button"
                    onClick={() => setShowFusionHelp(true)}
                    className={`text-slate-400 transition-colors focus:outline-none flex items-center justify-center ${isDarkMode ? 'hover:text-blue-400' : 'hover:text-blue-600'}`}
                    title="Explicación de Modos"
                  >
                    <HelpCircle size={11} className="sm:w-[12px] sm:h-[12px] stroke-[2.5]" />
                  </button>
                </div>
              </div>
            </div>

              {/* Bloque Visual de Auditoría con Tarjeta Integrada de Confirmación */}
              {rawAuditResult && (
                <div
                  className={`mt-4 w-full p-4 rounded-2xl border flex flex-col gap-3 animate-in zoom-in-95 duration-200 text-xs shadow-md ${
                    isDarkMode ? 'bg-slate-900/60 border-indigo-900/40' : 'bg-indigo-50/40 border-indigo-100'
                  }`}
                >
                  <div className="flex items-center justify-between pb-2 border-b ${isDarkMode ? 'border-indigo-900/30' : 'border-indigo-100/50'}">
                    <div className="flex items-center space-x-2">
                      <ShieldCheck size={16} className={isDarkMode ? "text-indigo-400" : "text-indigo-600"} />
                      <span className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-indigo-300' : 'text-indigo-900'}`}>
                        Auditoría Médica
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          size={12}
                          className={s <= (rawAuditResult.rating || 0) ? 'text-yellow-500 fill-yellow-500' : (isDarkMode ? 'text-gray-700' : 'text-gray-300')}
                        />
                      ))}
                    </div>
                  </div>

                  <div
                    className={`text-xs leading-relaxed font-medium rad-html-content ${isDarkMode ? 'text-gray-200' : 'text-slate-800'}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(mdToHtml(rawAuditResult.recommendations)) }}
                  />

                  {/* Tarjeta de Confirmación Integrada (Iframe Safe) */}
                  <div
                    className={`mt-2 p-3 rounded-xl border flex flex-col sm:flex-row items-center justify-between gap-3 ${
                      isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-indigo-100 shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}">
                      <Sparkles size={14} className="text-indigo-500 shrink-0" />
                      <span className="font-semibold">¿Aplicar los cambios sugeridos por la auditoría?</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setRawAuditResult(null)}
                        disabled={isApplyingAudit}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}"
                      >
                        Cerrar
                      </button>
                      <button
                        type="button"
                        disabled={isApplyingAudit}
                        onClick={handleApplyAuditSuggestions}
                        className="px-3.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm flex items-center gap-1.5 active:scale-95 transition-all disabled:opacity-50"
                      >
                        {isApplyingAudit ? (
                          <>
                            <RotateCw size={11} className="animate-spin" />
                            <span>Aplicando...</span>
                          </>
                        ) : (
                          <>
                            <Check size={12} strokeWidth={3} />
                            <span>Aplicar a Hallazgos y Conclusión</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>

          {generatedResult && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 mt-4 px-2">
                <div className="flex items-center space-x-2 text-indigo-600">
                  <Sparkles size={18} className="animate-pulse" />
                  <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                    INFORME FUSIONADO PhD ({formatTime(processSeconds)})
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {processSeconds > 0 && (
                    <div className="flex items-center space-x-1.5 opacity-50">
                      <Timer size={12} className="text-gray-400" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                        Tiempo de proceso: {formatTime(processSeconds)}
                      </span>
                    </div>
                  )}
                  {lastGenerationTiming && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                      <Clock size={11} />
                      <span>Este informe: {formatTime(lastGenerationTiming.dictationSeconds)} dictado + {formatTime(lastGenerationTiming.fusionSeconds)} fusión</span>
                    </div>
                  )}
                  {monthlyAvgTiming ? (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-500 dark:text-indigo-400">
                      <TrendingUp size={11} />
                      <span>Promedio este mes ({monthlyAvgTiming.count} informes): {formatTime(Math.round(monthlyAvgTiming.avgDictation))} dictado + {formatTime(Math.round(monthlyAvgTiming.avgFusion))} fusión</span>
                      <button
                        type="button"
                        onClick={loadMonthlyTimingAverage}
                        disabled={isLoadingMonthlyAvg}
                        title="Actualizar promedio del mes"
                        className="p-0.5 rounded hover:bg-indigo-500/10 disabled:opacity-50"
                      >
                        <RotateCw size={11} className={isLoadingMonthlyAvg ? "animate-spin" : ""} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={loadMonthlyTimingAverage}
                      disabled={isLoadingMonthlyAvg}
                      className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-500 dark:text-indigo-400 hover:underline disabled:opacity-50"
                    >
                      {isLoadingMonthlyAvg ? <RotateCw size={11} className="animate-spin" /> : <TrendingUp size={11} />}
                      <span>{monthlyAvgRequested ? "Sin informes este mes" : "Ver promedio del mes"}</span>
                    </button>
                  )}
                </div>
              </div>
              <ResultBox
                title="HALLAZGOS"
                content={generatedResult.findings}
                icon={<FileText size={16} />}
                color="blue"
                isDarkMode={isDarkMode}
                onAudit={handleInlineAudit}
                onPostProcess={(action: any, lang?: string) =>
                  handlePostProcess("findings", action, lang)
                }
                onNavigateHistory={(idx: number) => {
                  setSectionIndices(prev => ({ ...prev, findings: idx }));
                  setGeneratedResult(prev => prev ? { ...prev, findings: sectionHistories.findings[idx] } : null);
                }}
                isProcessing={isPostProcessing === "findings" || isInlineAuditing}
                versionIndex={sectionIndices.findings}
                historyCount={sectionHistories.findings.length}
              />

              {/* Auditoría en Línea bajo Hallazgos */}
              {isInlineAuditing && (
                <div className={`p-4 rounded-2xl border border-dashed flex items-center justify-center space-x-3 mt-2 ${isDarkMode ? 'border-indigo-500/30 bg-indigo-950/10 text-indigo-400' : 'border-indigo-200 bg-indigo-50/20 text-indigo-600'}`}>
                  <RotateCw size={16} className="animate-spin" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Ejecutando Auditoría PhD...</span>
                </div>
              )}

              {inlineAuditResult && (
                <div className={`p-5 rounded-2xl border shadow-lg mt-3 flex flex-col md:flex-row gap-4 items-start justify-between animate-in slide-in-from-top-2 duration-300 ${isDarkMode ? 'bg-slate-900/60 border-indigo-900/30' : 'bg-indigo-50/40 border-indigo-100'}`}>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-indigo-600 p-1.5 rounded-lg text-white">
                        <Star size={16} />
                      </div>
                      <h4 className={`text-[11px] font-black uppercase tracking-wider ${isDarkMode ? 'text-indigo-300' : 'text-indigo-900'}`}>
                        Resultado de la Auditoría (Findings)
                      </h4>
                      <div className={`flex items-center space-x-0.5 p-1 rounded-lg border ${isDarkMode ? 'bg-black/20 border-gray-700' : 'bg-white border-gray-100'}`}>
                        {[1,2,3,4,5].map((s) => (
                          <Star key={s} size={14} className={s <= (inlineAuditResult.rating || 0) ? 'text-yellow-500 fill-yellow-500' : (isDarkMode ? 'text-gray-700' : 'text-gray-200')} />
                        ))}
                      </div>
                    </div>
                    <div 
                      className={`text-[12.5px] leading-relaxed font-medium rad-html-content ${isDarkMode ? 'text-gray-300' : 'text-slate-700'}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(mdToHtml(inlineAuditResult.recommendations)) }}
                    />
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-full md:w-auto self-stretch justify-center">
                    <button
                      onClick={handleIntegrateInlineAudit}
                      disabled={isIntegratingAudit}
                      className={`px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-md transition-all active:scale-[0.98] flex items-center justify-center space-x-2 ${isIntegratingAudit ? 'bg-indigo-400 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-600/10'}`}
                    >
                      {isIntegratingAudit ? (
                        <>
                          <RotateCw size={14} className="animate-spin" />
                          <span>Integrando...</span>
                        </>
                      ) : (
                        <>
                          <Wand2 size={14} />
                          <span>Integrar Auditoría</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setInlineAuditResult(null)}
                      className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border ${isDarkMode ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-slate-200 text-slate-500 hover:bg-slate-100'}`}
                    >
                      Ocultar
                    </button>
                  </div>
                </div>
              )}
              <ResultBox
                title={activeRegionalSettings.renameConclusionToSummary ? "RESUMEN DE HALLAZGOS PRINCIPALES" : "CONCLUSIÓN"}
                helpText="Síntesis de los hallazgos ya dictados por el radiólogo, sin análisis diagnóstico adicional ni diferenciales."
                content={generatedResult.conclusion}
                icon={<Check size={16} />}
                color="green"
                isDarkMode={isDarkMode}
                onPostProcess={(action: any, lang?: string) =>
                  handlePostProcess("conclusion", action, lang)
                }
                onNavigateHistory={(idx: number) => {
                  setSectionIndices(prev => ({ ...prev, conclusion: idx }));
                  setGeneratedResult(prev => prev ? { ...prev, conclusion: sectionHistories.conclusion[idx] } : null);
                }}
                isProcessing={isPostProcessing === "conclusion"}
                versionIndex={sectionIndices.conclusion}
                historyCount={sectionHistories.conclusion.length}
              />
              {generatedResult.differential && !activeRegionalSettings.disableDiferenciales && (
                <ResultBox
                  title="DIFERENCIAL"
                  content={generatedResult.differential}
                  icon={<Sparkles size={16} />}
                  color="purple"
                  isDarkMode={isDarkMode}
                  onPostProcess={(action: any, lang?: string) =>
                    handlePostProcess("differential", action, lang)
                  }
                  onNavigateHistory={(idx: number) => {
                    setSectionIndices(prev => ({ ...prev, differential: idx }));
                    setGeneratedResult(prev => prev ? { ...prev, differential: sectionHistories.differential[idx] } : null);
                  }}
                  isProcessing={isPostProcessing === "differential"}
                  versionIndex={sectionIndices.differential}
                  historyCount={sectionHistories.differential.length}
                />
              )}
              <div className="flex justify-center pt-4">
                <button
                  onClick={handleReset}
                  className="text-[10px] font-black uppercase tracking-widest text-blue-500 flex items-center gap-2 border border-blue-500/20 px-10 py-3.5 rounded-full hover:bg-blue-50 transition-all active:scale-95 shadow-sm"
                >
                  COMENZAR NUEVO INFORME
                </button>
              </div>
            </div>
          )}
        </div>

        {isWide && window.innerWidth >= 1024 && (
          <div
            className={`shrink-0 w-[420px] border-l overflow-y-auto custom-scrollbar hidden lg:block ${isDarkMode ? "border-gray-700 bg-[#2a2d34]/30" : "border-slate-200 bg-slate-50/50"}`}
          >
            <div className="p-8 space-y-6">
              <div
                ref={reportSearchContainerRef}
                className={`rounded-[24px] shadow-sm border p-6 ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-slate-200"}`}
              >
                <label className="text-[11px] font-black text-slate-400 uppercase mb-4 block tracking-[0.15em]">
                  1. INFORME BASE DE NORMALIDAD
                </label>
                <div className="relative mb-4">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                  />
                  <input
                    type="text"
                    value={reportSearch}
                    onChange={(e) => setReportSearch(e.target.value)}
                    onKeyDown={handleReportSearchKeyDown}
                    placeholder="Buscar informe normal..."
                    className={`w-full pl-10 pr-10 py-3.5 border rounded-2xl text-sm font-bold outline-none transition-all ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white" : "bg-slate-50 border-slate-100 text-slate-900 focus:bg-white focus:border-blue-400"}`}
                  />
                  <button
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${showFavoritesOnly ? 'text-amber-500 bg-amber-500/10' : 'text-gray-300 hover:text-amber-500'}`}
                    title="Filtrar Favoritos MODULAR-1"
                  >
                     <Heart size={16} fill={showFavoritesOnly ? "currentColor" : "none"} />
                  </button>
                </div>

                <div className="flex items-center gap-1.5 overflow-x-auto mb-4 pb-1 -mx-1 px-1 custom-scrollbar">
                  {mod1ReportCategories.map(cat => {
                    const isActive = activeCategoryFilterId === cat.id;
                    const isEditing = categoryEditModeId === cat.id;
                    return (
                      <div
                        key={cat.id}
                        className={`shrink-0 flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg text-[9px] font-black uppercase tracking-wide border transition-all whitespace-nowrap ${
                          isEditing ? 'bg-emerald-600 border-emerald-600 text-white' : isActive ? 'bg-blue-600 border-blue-600 text-white' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500')
                        }`}
                      >
                        <button type="button" onClick={() => setActiveCategoryFilterId(isActive ? null : cat.id)} className="whitespace-nowrap">
                          {cat.name}
                        </button>
                        <button type="button" onClick={() => setCategoryEditModeId(isEditing ? null : cat.id)} title="Editar informes de esta categoría" className="opacity-70 hover:opacity-100 p-0.5">
                          <Pencil size={8} />
                        </button>
                        <button type="button" onClick={() => handleDeleteCategory(cat.id)} title="Eliminar categoría" className="opacity-70 hover:opacity-100 p-0.5">
                          <X size={9} />
                        </button>
                      </div>
                    );
                  })}
                  {isAddingCategory ? (
                    <div className="shrink-0 flex items-center gap-1">
                      <input
                        type="text"
                        autoFocus
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateCategory();
                          if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(""); }
                        }}
                        placeholder="Nombre..."
                        className={`w-24 px-2 py-1 rounded-lg border text-[9px] font-bold outline-none ${isDarkMode ? "bg-[#1e2024] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-800"}`}
                      />
                      <button type="button" onClick={handleCreateCategory} className="p-1 rounded-lg bg-emerald-500 text-white"><Check size={10} /></button>
                      <button type="button" onClick={() => { setIsAddingCategory(false); setNewCategoryName(""); }} className="p-1 rounded-lg text-gray-400"><X size={10} /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setIsAddingCategory(true)} className={`shrink-0 p-1.5 rounded-lg border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-blue-400' : 'bg-white border-gray-200 text-gray-400 hover:text-blue-600'}`} title="Crear categoría">
                      <Plus size={10} />
                    </button>
                  )}
                </div>

                {reportSearch &&
                  !selectedReport &&
                  filteredReports.length > 0 && (
                    <div
                      className={`border rounded-2xl overflow-hidden shadow-2xl mb-4 animate-in slide-in-from-top-1 max-h-[400px] overflow-y-auto custom-scrollbar ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-slate-100"}`}
                    >
                      {filteredReports.slice(0, 40).map((r) => {
                        const isMod1Fav = mod1Favorites.includes(r.id);
                        return (
                        <div key={r.id} className="flex items-center border-b last:border-none group/item">
                          <button
                            onClick={() => {
                              setSelectedReportId(r.id);
                              setReportSearch("");
                            }}
                            className={`flex-1 text-left px-5 py-3 text-[11px] font-black uppercase transition-colors ${isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-slate-900 hover:bg-blue-50"}`}
                          >
                            {r.title}
                          </button>
                          <div className="flex items-center pr-3">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onEditReport?.(r.id); }}
                              title="Editar este informe base"
                              className={`p-2 opacity-30 group-hover/item:opacity-100 hover:!opacity-100 transition-all text-gray-400 hover:text-indigo-500 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-indigo-50/50'}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleCopyReportContent(e, r)}
                              title="Copiar contenido del informe"
                              className={`p-2 opacity-30 group-hover/item:opacity-100 hover:!opacity-100 transition-all text-gray-400 hover:text-blue-500 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-blue-50/50'}`}
                            >
                              {copiedReportId === r.id ? (
                                <Check size={14} className="text-emerald-500" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                            <button 
                              onClick={(e) => toggleMod1Favorite(e, r.id)}
                              className={`p-2 opacity-30 group-hover/item:opacity-100 transition-all rounded-lg ${isMod1Fav ? 'text-amber-500 opacity-100' : 'text-gray-400 hover:text-amber-500'}`}
                            >
                              <Heart size={14} fill={isMod1Fav ? "currentColor" : "none"} />
                            </button>
                          </div>
                        </div>
                      );})}
                    </div>
                  )}

                {selectedReport && (
                  <div className={`p-5 rounded-2xl flex justify-between items-center border animate-in zoom-in-95 ${isDarkMode ? 'bg-blue-900/10 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center space-x-4 truncate">
                      <div className="bg-blue-600 p-2 rounded-xl text-white shadow-lg shadow-blue-600/20">
                        <Check size={16} strokeWidth={4} />
                      </div>
                      <span
                        className={`text-[12px] font-black truncate uppercase tracking-tight ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                      >
                        {selectedReport.title}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedReportId(null)}
                      className={`p-2 rounded-xl transition-all shadow-sm ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-white'}`}
                    >
                      <X
                        size={16}
                        className="text-slate-400 hover:text-red-500"
                      />
                    </button>
                  </div>
                )}

                {!reportSearch && renderFrequentReportsList()}

                <div
                  className={`mt-6 p-4 rounded-2xl border ${isDarkMode ? "bg-indigo-900/5 border-indigo-900/20" : "bg-indigo-50/30 border-indigo-100"}`}
                >
                  <span className="text-[8px] font-black text-indigo-500 uppercase mb-3 block tracking-widest text-center">
                    Arquitectura de Voz
                  </span>
                  <div
                    className={`flex flex-wrap gap-1 rounded-lg p-0.5 border shadow-inner ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-indigo-50"}`}
                  >
                    <button
                      onClick={() => setEngineMode("phd")}
                      className={`flex-1 min-w-[60px] py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "phd" ? "bg-blue-600 text-white shadow-md" : "text-gray-400 hover:text-gray-500"}`}
                    >
                      <GraduationCap size={12} className="mr-1" /> PHD
                    </button>
                    <button
                      onClick={() => setEngineMode("gm1")}
                      className={`flex-1 min-w-[60px] py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "gm1" ? "bg-indigo-600 text-white shadow-md" : "text-gray-400 hover:text-gray-500"}`}
                    >
                      <Activity size={12} className="mr-1" /> GM1
                    </button>
                  </div>
                </div>
              </div>
              <div
                className={`rounded-[32px] shadow-sm border p-6 space-y-6 ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-slate-200"}`}
              >
                <div className="flex items-center space-x-3 text-indigo-500">
                  <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-indigo-900/20' : 'bg-indigo-50'}`}>
                    <Wand2 size={18} />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-widest">
                    MOTOR DE FUSIÓN PhD
                  </span>
                </div>
                <p className={`text-[11px] leading-relaxed font-medium ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                  El sistema integrará tus hallazgos dictados en la arquitectura
                  del informe base, manteniendo la estructura técnica original.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {showFusionHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-200">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 border ${isDarkMode ? 'bg-slate-900 border-gray-800 text-white' : 'bg-white border-slate-100 text-gray-900'} animate-in zoom-in-95 duration-200`}>
            <div className={`flex items-center justify-between mb-4 pb-2 border-b ${isDarkMode ? 'border-gray-800' : 'border-slate-100'}`}>
              <div className="flex items-center gap-2">
                <HelpCircle size={18} className="text-[#2563eb]" />
                <span className="font-extrabold text-[12.5px] uppercase tracking-wider font-['Outfit']">Modos de Fusión y Ejecución</span>
              </div>
              <button 
                type="button"
                onClick={() => setShowFusionHelp(false)}
                className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-slate-100'}`}
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="space-y-4 text-[11px] leading-relaxed">
              <div>
                <h4 className="font-black text-[#2563eb] mb-1 text-[11px] uppercase tracking-wide">⚡ Fast Fusion (Rápido)</h4>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  Modelo ultra-rápido diseñado para dictados directos. Realiza reemplazos quirúrgicos (bisturí semántico) para insertar hallazgos del audio manteniendo el resto de la plantilla completamente intacta.
                </p>
              </div>

              <div>
                <h4 className="font-black text-indigo-500 mb-1 text-[11px] uppercase tracking-wide">🧠 Normal/Pro Fusion (Élite)</h4>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  Modelo inteligente con mayor capacidad de razonamiento clínico. Asegura una alineación estructural coherente, elimina contradicciones complejas y re-organiza hallazgos graves al inicio del reporte de manera inteligente.
                </p>
              </div>

              <div>
                <h4 className="font-black text-emerald-500 mb-1 text-[11px] uppercase tracking-wide">🍃 Modo Flexible (Alternativo)</h4>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  Activa políticas alternativas optimizadas para fusionar plantillas con mayor dinamismo. Prioriza estrictamente la verdad clínica, adaptando las subdivisiones y el diseño del reporte original con total adaptabilidad ante patologías severas.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowFusionHelp(false)}
                className="px-4 py-2 bg-[#2563eb] text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-blue-700 transition-colors active:scale-95"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

    <RetryTranscriptionButton 
        isDark={isDarkMode}
        moduleName="modular1"
        onRetry={async (backup) => {
          setIsProcessingAudio(true);
          try {
            const transcription = await transcribeAudio(backup.blobBase64, backup.mimeType, backup.engine || "phd");

            if (transcription && transcription.trim().length > 1) {
              if (anchorMode) {
                const baseText = localFullText || fullText || "";
                const pos = Math.min(Math.max(anchorPosRef.current, 0), baseText.length);
                const before = baseText.substring(0, pos);
                const after = baseText.substring(pos);
                const insertion = buildAnchorInsertion(before, transcription, after);
                const newVal = before + insertion + after;
                anchorPosRef.current = before.length + insertion.length;
                handleTextChange(newVal);
              } else {
                const newVal = !fullText || fullText.trim() === ""
                  ? transcription
                  : fullText + "\n" + transcription;
                handleTextChange(newVal);
              }
            }
          } catch (error) {
            alert("El reintento ha fallado. Verifique su conexión.");
            throw error;
          } finally {
            setIsProcessingAudio(false);
          }
        }}
      />
    </div>
  );
};

export default Modular1Screen;
